import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import TranscriptionMathRenderer from '../components/TranscriptionMathRenderer';
import Card from '../components/ui/Card';
import AnswerCapture from '../components/tutor/AnswerCapture';
import BottomBar from '../components/tutor/BottomBar';
import { shouldShowDebugUI } from '../components/debug/debugGate';
import {
  TutorSessionDebugControls,
  TutorSessionDebugHeader,
  TutorSessionDebugInsight,
  TutorSessionDebugPanelBlock,
  TutorSessionProgressionDebug,
} from '../generated/debugToolLoaders';
import {
  buildImageSubmissionFormData,
  buildTextSubmissionFormData,
} from '../utils/imageUpload';
import {
  describeSuccessCriteria,
  describeNextStepHint,
  describeSelectionReason,
  getAttempt,
  getAttemptDraft,
  getAttemptState,
  getSession,
  getSessionState,
  getMasteryInspectWithFallback,
  postAbandonAttempt,
  postEndSession,
  postConfirmPartAttemptEvidence,
  patchConfirmPartAttemptTranscription,
  postFinalizeAttempt,
  postMarkPartAttempt,
  postNextQuestion,
  postOverridePartAttempt,
  postSkipPartAttempt,
  postSkipPartByLabel,
  postSubmitPart,
  normalizeMasterySelection,
  putAttemptDraft,
} from '../api/tutor';
import { getStoredUser, nowIso } from '../api/client';
import { useReportIssueContext } from '../reportIssueContext';
import { formatState } from '../lib/format';
import {
  SUBMISSION_PROCESSING_UNAVAILABLE_CODE,
  SUBMISSION_PROCESSING_UNAVAILABLE_COPY,
  SUBMISSION_UNREADABLE_COPY,
} from '../copy/submissionStatus';
import {
  STUDENT_DEGRADED_PAGE_GUIDANCE,
  STUDENT_SUBMISSION_RECOVERY_GUIDANCE,
  STUDENT_SUBMISSION_TRUST_RECOVERY_COPY,
  SUBMISSION_TRUST_RECOVERY_HEADING,
  TUTOR_DEGRADED_PAGE_GUIDANCE,
  TUTOR_SUBMISSION_RECOVERY_GUIDANCE,
  TUTOR_SUBMISSION_TRUST_RECOVERY_COPY,
} from '../copy/submissionTrust';
import { normalizeGeneratedFeedbackMarkdown } from '../utils/feedbackMarkdown';
import { useSessionRuntimeSession } from '../components/SessionRuntimeContext';

const FEEDBACK_POLL_STALE_AFTER_MS = 60000;
const FEEDBACK_POLL_MAX_DELAY_MS = 5000;
const DRAFT_LEAVE_WARNING =
  'You have an in-progress drawing, unsaved override changes, or selected pages that have not been submitted. Leave this question?';
const TUTOR_SESSION_DRAFT_PREFIX = 'sumrise.tutorSessionDraft.';
const TUTOR_SESSION_DRAFT_WRITE_DELAY_MS = 500;

// Tutor routing keys come from mastery selection responses, not from the
// question's display-name fields. Exported so this endpoint-specific mapping
// is covered without a full TutorSession render harness.
export function buildTutorReportContext({
  sessionId,
  attemptId,
  question,
  masterySelection,
  selectionMeta,
}) {
  return {
    session_id: sessionId ? Number(sessionId) || sessionId : null,
    attempt_id: attemptId || null,
    question_id: question?.id ?? null,
    family_key: masterySelection?.family_key || selectionMeta?.family_key || null,
    skill_code: masterySelection?.skill_code || selectionMeta?.skill_code || null,
    micro_skill_code:
      masterySelection?.micro_skill_code || selectionMeta?.micro_skill_code || null,
  };
}

function getTutorSessionDraftKey(sessionId) {
  return `${TUTOR_SESSION_DRAFT_PREFIX}${sessionId}`;
}

function fileToStoredFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
      dataUrl: reader.result,
    });
    reader.onerror = () => reject(reader.error || new Error('Failed to preserve selected file.'));
    reader.readAsDataURL(file);
  });
}

async function serializeFilesByPart(filesByPart) {
  const entries = await Promise.all(
    Object.entries(filesByPart).map(async ([partLabel, files]) => [
      partLabel,
      await Promise.all((files || []).filter(Boolean).map(fileToStoredFile)),
    ])
  );
  return Object.fromEntries(entries);
}

function storedFileToFile(storedFile) {
  const [, base64 = ''] = String(storedFile?.dataUrl || '').split(',', 2);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new File([bytes], storedFile.name, {
    type: storedFile.type,
    lastModified: storedFile.lastModified,
  });
}

function restoreStoredFiles(filesByPart) {
  return Object.fromEntries(
    Object.entries(filesByPart || {}).map(([partLabel, files]) => [
      partLabel,
      (files || []).map(storedFileToFile),
    ])
  );
}

function readTutorSessionDraft(sessionId) {
  if (!sessionId) return null;
  try {
    const stored = JSON.parse(sessionStorage.getItem(getTutorSessionDraftKey(sessionId)));
    if (!stored?.question || !stored?.attemptId) return null;
    return {
      ...stored,
      serializedFilesByPart: stored.filesByPart || {},
      filesByPart: restoreStoredFiles(stored.filesByPart),
    };
  } catch (_error) {
    return null;
  }
}

function getOverrideDraftStatusCopy(status) {
  if (status === 'dirty') {
    return 'Override draft has unsaved changes.';
  }
  if (status === 'saving') {
    return 'Saving override draft...';
  }
  if (status === 'saved') {
    return 'Override draft saved.';
  }
  if (status === 'failed') {
    return 'Could not save override draft. Keep this page open and try again before leaving.';
  }
  if (status === 'permission_denied') {
    return 'You do not have permission to save marking overrides.';
  }
  return '';
}

function getPartTotals(part) {
  const t = part?.mark_json?.total;
  const score = typeof t?.score === 'number' ? t.score : null;
  const max = typeof t?.max === 'number' ? t.max : null;
  return { score, max, isMarked: score !== null && max !== null };
}

function parseRubricMaxes(rubricJson) {
  const parsed =
    typeof rubricJson === 'string'
      ? (() => {
          try {
            return JSON.parse(rubricJson);
          } catch (error) {
            return {};
          }
        })()
      : rubricJson || {};

  const answerMax =
    typeof parsed.answer_max === 'number' && parsed.answer_max >= 0
      ? parsed.answer_max
      : 0;
  const methodMax =
    typeof parsed.method_max === 'number' && parsed.method_max >= 0
      ? parsed.method_max
      : 0;
  const communicationMax =
    typeof parsed.comm_max === 'number' && parsed.comm_max >= 0
      ? parsed.comm_max
      : 0;

  return { answerMax, methodMax, communicationMax };
}

function getPartRubricMaxes(part, workspacePart) {
  const rubricMaxes = parseRubricMaxes(part?.rubric_json);
  const markedMaxes = {
    answerMax: workspacePart?.mark_json?.answer?.max,
    methodMax: workspacePart?.mark_json?.method?.max,
    communicationMax: workspacePart?.mark_json?.communication?.max,
  };

  return {
    answerMax: typeof markedMaxes.answerMax === 'number' ? markedMaxes.answerMax : rubricMaxes.answerMax,
    methodMax: typeof markedMaxes.methodMax === 'number' ? markedMaxes.methodMax : rubricMaxes.methodMax,
    communicationMax:
      typeof markedMaxes.communicationMax === 'number'
        ? markedMaxes.communicationMax
        : rubricMaxes.communicationMax,
  };
}

function pickFirstDefined(...values) {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return null;
}

function isLikelyInternalLabel(value) {
  const text = String(value || '').trim();
  if (!text) {
    return true;
  }
  if (/\s/.test(text)) {
    return false;
  }
  const hasLowercase = /[a-z]/.test(text);
  const hasDigit = /\d/.test(text);
  const hasCodeSeparator = /[-_:/\\]/.test(text);
  if (!hasLowercase && (hasDigit || hasCodeSeparator)) {
    return true;
  }
  return false;
}

function readMeaningfulLabel(...values) {
  for (let index = 0; index < values.length; index += 1) {
    const text = String(values[index] || '').trim();
    if (text && !isLikelyInternalLabel(text)) {
      return text;
    }
  }
  return null;
}

function stripMarkdownToPlainText(markdown) {
  return String(markdown || '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/!\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/[`*_>#~|-]/g, ' ')
    .replace(/\$+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildStemTitleFallback(stemMarkdown) {
  const stemText = stripMarkdownToPlainText(stemMarkdown);
  if (!stemText) {
    return 'Question';
  }
  return stemText.length > 72 ? `${stemText.slice(0, 69).trimEnd()}...` : stemText;
}

function getLearningContextLabels(question, selectionTarget) {
  const metadata = question?.generator_metadata_json || {};
  const payloadLearningContext = question?.learning_context || {};

  const familyLabel = readMeaningfulLabel(
    payloadLearningContext.title,
    question?.family_name,
    question?.topic_group_name,
    question?.topic_name,
    metadata.topic_name,
    metadata.family_name
  );
  const macroSkillLabel = readMeaningfulLabel(
    question?.skill_name,
    selectionTarget?.skill_name,
    question?.focus_name,
    selectionTarget?.focus_name,
    metadata.skill_name
  );
  const microSkillLabel = readMeaningfulLabel(
    question?.micro_skill_name,
    selectionTarget?.micro_skill_name,
    metadata.micro_skill_name
  );

  const title =
    familyLabel ||
    macroSkillLabel ||
    microSkillLabel ||
    buildStemTitleFallback(question?.stem_markdown);

  const subtitle = readMeaningfulLabel(
    payloadLearningContext.subtitle,
    title === familyLabel
      ? macroSkillLabel || microSkillLabel
      : title === macroSkillLabel
      ? microSkillLabel
      : null
  );

  return { title, subtitle };
}

function normalizeComparableText(markdown) {
  return stripMarkdownToPlainText(markdown).toLowerCase();
}

function isBareInstructionLabel(normalizedStem) {
  return /^(?:calculate|choose|compare|complete|construct|convert|describe|determine|draw|estimate|evaluate|expand|explain|express|factorise|factorize|find|give|identify|measure|order|plot|prove|rearrange|round|select|show|simplify|solve|state|work out|write)\b/.test(
    normalizedStem
  );
}

function shouldRenderSharedQuestionStem({ stemMarkdown, firstPartPrompt }) {
  if (!String(stemMarkdown || '').trim()) {
    return false;
  }
  const normalizedStem = normalizeComparableText(stemMarkdown);
  const normalizedPrompt = normalizeComparableText(firstPartPrompt);

  if (!normalizedStem) {
    return false;
  }
  if (!normalizedPrompt) {
    return false;
  }
  if (normalizedStem === normalizedPrompt) {
    return false;
  }

  const stemRaw = String(stemMarkdown || '').trim();
  const hasStructuredContext =
    /\n\s*\n/.test(stemRaw) ||
    /!\[.*?\]\(.*?\)|\|.*\||^\s*[-*]\s+/m.test(stemRaw) ||
    normalizedStem.length > 120 ||
    /[.?!].+[.?!]/.test(normalizedStem);

  return hasStructuredContext || !isBareInstructionLabel(normalizedStem);
}

function mergeStemIntoFirstPart(stemMarkdown, promptMarkdown) {
  const stem = String(stemMarkdown || '').trim();
  const prompt = String(promptMarkdown || '').trim();

  if (!stem) {
    return prompt || null;
  }
  if (!prompt) {
    return stem;
  }
  if (normalizeComparableText(stem) === normalizeComparableText(prompt)) {
    return prompt;
  }
  if (!shouldRenderSharedQuestionStem({ stemMarkdown: stem, firstPartPrompt: prompt })) {
    return prompt;
  }
  return `${stem}\n\n${prompt}`;
}

function getReadableFocusLabel(target) {
  if (!target || typeof target !== 'object') {
    return null;
  }
  const candidates = [
    target.micro_skill_name,
    target.skill_name,
    target.focus_name,
    target.title,
    target.name,
  ];
  for (let index = 0; index < candidates.length; index += 1) {
    const text = String(candidates[index] || '').trim();
    if (!text || isLikelyInternalLabel(text)) {
      continue;
    }
    return text;
  }
  return null;
}

function formatTutorWhyNow({ selectionIntent, supportStage, fallback }) {
  if (selectionIntent === 'prerequisite_refresh') {
    return 'You need this prerequisite first. Secure it here so the session can move on safely.';
  }
  if (supportStage === 'worked_example') {
    return 'Review this method guidance before trying the skill more independently.';
  }
  if (supportStage === 'partially_supported') {
    return 'You are still on the same skill. Use the reduced support to show what you can now do yourself.';
  }
  if (supportStage === 'independent') {
    return "You're continuing this question — finish it properly so we can see what you can do.";
  }

  if (!fallback) {
    return null;
  }

  if (/weak|revisit|review/i.test(fallback)) {
    return 'This is coming back because it still needs stronger evidence. Show the method cleanly and finish it well.';
  }
  if (/progress|continue|next/i.test(fallback)) {
    return "You're moving forward on this topic. Finish this question cleanly so the session can judge what comes next.";
  }

  return 'Work through this carefully so the session gets a clear picture of what you can do.';
}

function formatTutorSuccessFocus({ fallback }) {
  if (!fallback) {
    return 'Make your working clear and make sure the final answer is correct.';
  }
  if (/method|working/i.test(fallback) && /answer/i.test(fallback)) {
    return 'Make sure your working is clear and your final answer is correct.';
  }
  if (/method|working/i.test(fallback)) {
    return 'Show the method clearly so the tutor can see how you reached the answer.';
  }
  if (/answer/i.test(fallback)) {
    return 'Focus on a correct final answer and make it easy to check.';
  }

  return 'Make the method easy to follow and finish with a clear final answer.';
}

function formatTutorLikelyNext({ fallback }) {
  if (!fallback) {
    return "Once you're done, I'll decide what to give you next.";
  }
  if (/keep checking|return to the next priority|likely/i.test(fallback)) {
    return "Once you're done, I'll decide what to give you next.";
  }
  if (/same skill|repeat/i.test(fallback)) {
    return 'If this still needs work, we will stay on the same skill for another question.';
  }

  return "Finish this first, then I'll decide the best next step.";
}

function formatTutorSupportPath({ supportStageLabel, headline, detail }) {
  if (supportStageLabel) {
    return `This is ${supportStageLabel.toLowerCase()} support — stay on task and use only the help that is currently available.`;
  }
  if (headline) {
    return headline;
  }
  if (detail) {
    return detail;
  }
  return null;
}

function buildTutorGuidanceSummary({ whyNow, successFocus }) {
  const lead = String(whyNow || '').trim();
  const follow = String(successFocus || '').trim();
  if (/prerequisite/i.test(lead)) {
    return 'Secure this prerequisite before moving on.';
  }
  if (/worked example/i.test(lead)) {
    return 'Review the method guidance, then try the method yourself.';
  }
  if (/working|method/i.test(follow) || /final answer/i.test(follow)) {
    return 'Finish this cleanly — show your working and a clear final answer.';
  }
  if (lead) {
    return lead;
  }
  return follow || null;
}

function hasSessionInsightSelection(target) {
  if (!target || typeof target !== 'object') {
    return false;
  }

  const hasReason =
    typeof target.reason === 'string' && Boolean(target.reason.trim());
  const hasReasonDetail =
    typeof target.reason_detail === 'string' && Boolean(target.reason_detail.trim());
  const hasReasonFlags =
    Array.isArray(target.reason_flags) &&
    target.reason_flags.some((flag) => typeof flag === 'string' && Boolean(flag.trim()));
  const hasSkillIdentity = [
    target.macro_skill_code,
    target.micro_skill_code,
    target.skill_code,
  ].some((value) => typeof value === 'string' && Boolean(value.trim()));

  return hasReason || hasReasonDetail || hasReasonFlags || hasSkillIdentity;
}

function buildWorkedExampleParts(tutorSupport) {
  const parts = Array.isArray(tutorSupport?.worked_example?.parts)
    ? tutorSupport.worked_example.parts
    : [];

  return parts
    .map((part, index) => {
      if (!part || typeof part !== 'object') {
        return null;
      }

      const expected = part.expected_answer_json || {};
      const expectedType = typeof expected.type === 'string' ? expected.type.trim() : '';
      const answer = String(part.canonical_answer_latex || '').trim();
      if (!answer) {
        return null;
      }

      let methodHint = String(part.method_description_markdown || '').trim();
      if (!methodHint && expectedType === 'CHECK_SUBSTITUTION') {
        const sourcePart = String(expected.x_part_label || '').trim();
        const lhsTemplate = String(expected.lhs_template_latex || '').trim();
        methodHint = sourcePart
          ? `Use your value from part ${sourcePart} to substitute into ${lhsTemplate || 'the equation'}, then check the result.`
          : 'Substitute the earlier result back into the equation to check it.';
      } else if (!methodHint && index > 0) {
        methodHint = 'Carry the earlier result forward, then complete this step before checking the model answer.';
      } else if (!methodHint) {
        methodHint = 'Work through the method cleanly, then compare your result with the model answer.';
      }

      return {
        partLabel: String(part.part_label || '').trim() || String(index + 1),
        promptSuffixMarkdown: String(part.prompt_suffix_markdown || '').trim(),
        methodHint,
        answer,
        answerMarkdown:
          /^[A-Za-z][A-Za-z\s-]*$/.test(answer) && !answer.includes('\\')
            ? answer
            : `$${answer}$`,
      };
    })
    .filter(Boolean);
}

function buildPrerequisiteRefreshCard(tutorSupport) {
  const card = tutorSupport?.prerequisite_refresh_card;
  if (!card || typeof card !== 'object') {
    return null;
  }

  const prerequisiteMicroSkillName = String(card.prerequisite_micro_skill_name || '').trim();
  if (!prerequisiteMicroSkillName) {
    return null;
  }

  const prerequisiteSkillName = String(card.prerequisite_skill_name || '').trim();

  return {
    prerequisiteMicroSkillName,
    prerequisiteSkillName,
  };
}

function buildExamStyleSupportCard(tutorSupport) {
  if (!tutorSupport || typeof tutorSupport !== 'object') {
    return null;
  }

  const supportStage = String(tutorSupport.support_stage || '').trim();
  const selectionIntent = String(tutorSupport.selection_intent || '').trim();
  if (!supportStage || selectionIntent === 'prerequisite_refresh') {
    return null;
  }

  if (supportStage === 'worked_example') {
    return {
      stageLabel: 'Worked example',
      headline: 'Review the method before exam-style independence',
      detail:
        'This still sits inside tutor mode. Start with the method guidance, then move toward a supported exam-style attempt on the same micro-skill.',
    };
  }

  if (supportStage === 'partially_supported') {
    return {
      stageLabel: 'Support stage',
      headline: 'Supported exam-style attempt',
      detail:
        'Keep the learner on the same micro-skill, but reduce the scaffolding. The aim is a more exam-style response without switching into a separate exam session.',
    };
  }

  if (supportStage === 'independent') {
    return {
      stageLabel: 'Support stage',
      headline: 'Independent exam-style attempt',
      detail:
        'This is the independent readiness step inside tutor mode. Expect a clean exam-style response without the worked-example scaffold.',
    };
  }

  return null;
}

function formatSupportStageLabel(stage) {
  const normalized = String(stage || '').trim();
  if (normalized === 'worked_example') {
    return 'Worked example';
  }
  if (normalized === 'partially_supported') {
    return 'Supported attempt';
  }
  if (normalized === 'independent') {
    return 'Independent attempt';
  }
  return null;
}

function formatSelectionIntentLabel(intent) {
  const normalized = String(intent || '').trim();
  if (normalized === 'progress_next') {
    return 'Forward progress';
  }
  if (normalized === 'prerequisite_refresh') {
    return 'Prerequisite refresh';
  }
  if (normalized === 'fluency_booster') {
    return 'Fluency booster';
  }
  if (normalized === 'review') {
    return 'Review';
  }
  if (normalized === 'repeat') {
    return 'Repeat';
  }
  return null;
}

function getAttemptTelemetry(attemptWorkspace, finalizeTelemetry) {
  return (
    attemptWorkspace?.telemetry ||
    attemptWorkspace?.attempt_telemetry ||
    finalizeTelemetry ||
    null
  );
}

function getTutorOverrideMetadata(workspacePart) {
  const directOverride = workspacePart?.tutor_override_json;
  if (directOverride && typeof directOverride === 'object') {
    return directOverride;
  }
  const markOverride = workspacePart?.mark_json?.tutor_override_json;
  if (markOverride && typeof markOverride === 'object') {
    return markOverride;
  }
  return null;
}

function hasTutorAdjustment(workspacePart) {
  const marks = workspacePart?.mark_json || {};
  const override = getTutorOverrideMetadata(workspacePart);
  const hasTutorOverride =
    Boolean(override?.override_adjusted) ||
    override?.evidence_source === 'tutor_adjusted' ||
    override?.evidence_integrity === 'tutor_adjusted_low_confidence_automatic' ||
    override?.reason === 'manual' ||
    override?.override_delta !== undefined ||
    override?.total_score !== undefined ||
    override?.answer_score !== undefined ||
    override?.method_score !== undefined ||
    override?.communication_score !== undefined;
  const hasOverrideDelta =
    marks.override_delta !== undefined && marks.override_delta !== null;
  const isManualReason =
    marks.reason === 'manual' || workspacePart?.reason === 'manual';
  const tutorNote =
    typeof workspacePart?.tutor_note === 'string'
      ? workspacePart.tutor_note
      : typeof marks.tutor_note === 'string'
      ? marks.tutor_note
      : '';
  const hasTutorNoteWhileMarked = getPartTotals(workspacePart).isMarked && Boolean(tutorNote.trim());
  const hasGenuineOverrideMetadata = hasTutorOverride || hasOverrideDelta || isManualReason;
  return hasGenuineOverrideMetadata || (!marks.skip && hasTutorNoteWhileMarked);
}

function getEvidenceConfirmationState(workspacePart) {
  const override = getTutorOverrideMetadata(workspacePart);
  const isConfirmed = Boolean(override?.tutor_confirmed);
  return {
    isConfirmed,
    confirmedLowConfidence: Boolean(
      isConfirmed && override?.confirmed_low_confidence_automatic
    ),
  };
}

function formatTrustValue(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, item]) => `${key} = ${item}`)
      .join(', ');
  }
  return String(value);
}

const SUBMISSION_TRUST_MESSAGES = {
  no_answer_found: 'We could not confidently identify a final answer.',
  multiple_answer_candidates: 'Multiple possible answers were detected.',
  ambiguous_final_answer: 'The final answer was ambiguous.',
  malformed_extraction: 'Your working was detected, but no clear markable final answer was found.',
  ocr_low_confidence: 'The answer reading may be uncertain. Check the interpreted answer before trusting the mark.',
  upload_rejected: 'The upload could not be accepted.',
  ocr_provider_failure: 'The submitted work could not be read. Please try again.',
  unsupported_image_heavy_task: 'This task may need tutor review before the mark is trusted.',
};

const TYPED_TRANSCRIPTION_SOURCE_LABELS = {
  student_typed: 'Answer source: student typed correction',
  staff_typed: 'Answer source: tutor or admin typed correction',
};

function getSubmissionTrustMessage(trust, categories) {
  const priority = [
    'no_answer_found',
    'multiple_answer_candidates',
    'ambiguous_final_answer',
    'malformed_extraction',
    'ocr_low_confidence',
    'unsupported_image_heavy_task',
    'ocr_provider_failure',
    'upload_rejected',
  ];
  const primaryCategory =
    typeof trust?.primary_category === 'string' ? trust.primary_category : '';
  const category =
    priority.find((item) => item === primaryCategory || categories.includes(item)) ||
    primaryCategory ||
    categories[0] ||
    '';
  if (category && SUBMISSION_TRUST_MESSAGES[category]) {
    return SUBMISSION_TRUST_MESSAGES[category];
  }
  if (typeof trust?.student_message === 'string' && trust.student_message.trim()) {
    return trust.student_message.trim();
  }
  return 'Check the interpreted answer before trusting this mark.';
}

function getSubmissionRecoveryGuidance(trust, canViewSessionInsights) {
  const categories = Array.isArray(trust?.categories) ? trust.categories : [];
  const primaryCategory =
    typeof trust?.primaryCategory === 'string' ? trust.primaryCategory : '';
  const hasCategory = (category) =>
    primaryCategory === category || categories.includes(category);

  if (
    hasCategory('no_answer_found') ||
    hasCategory('malformed_extraction') ||
    hasCategory('upload_rejected') ||
    hasCategory('ocr_provider_failure')
  ) {
    const kind = 'replace_or_override';
    return {
      kind,
      text: canViewSessionInsights
        ? TUTOR_SUBMISSION_RECOVERY_GUIDANCE[kind]
        : STUDENT_SUBMISSION_RECOVERY_GUIDANCE[kind],
    };
  }
  if (hasCategory('multiple_answer_candidates') || hasCategory('ambiguous_final_answer')) {
    const kind = 'override_when_clear';
    return {
      kind,
      text: canViewSessionInsights
        ? TUTOR_SUBMISSION_RECOVERY_GUIDANCE[kind]
        : STUDENT_SUBMISSION_RECOVERY_GUIDANCE[kind],
    };
  }
  if (hasCategory('ocr_low_confidence') || trust?.lowConfidence) {
    const kind = 'check_interpreted_answer';
    return {
      kind,
      text: canViewSessionInsights
        ? TUTOR_SUBMISSION_RECOVERY_GUIDANCE[kind]
        : STUDENT_SUBMISSION_RECOVERY_GUIDANCE[kind],
    };
  }
  return null;
}

const DEGRADED_PAGE_STATUSES = new Set(['failed', 'timeout', 'empty', 'partial', 'skipped']);
const PAGE_LEVEL_TRUST_REASONS = new Set([
  'partial_page_failure',
  'some_pages_empty',
  'mixed_page_confidence',
]);

function getPageStateLabel(status) {
  switch (status) {
    case 'timeout':
      return 'timed out during OCR';
    case 'empty':
      return 'had no readable working';
    case 'partial':
      return 'may be only partly readable';
    case 'failed':
      return 'could not be read';
    case 'skipped':
      return 'was skipped during OCR';
    default:
      return 'needs review';
  }
}

function getDegradedPageStates(workspacePart, trust) {
  const pageStates = workspacePart?.transcription?.confidence?.page_states;
  if (!Array.isArray(pageStates)) {
    return {
      shouldRender: false,
      degraded: [],
      totalPages: 0,
    };
  }
  const normalized = pageStates
    .map((page, index) => {
      if (!page || typeof page !== 'object') {
        return null;
      }
      const pageIndex = Number.isFinite(Number(page.page_index))
        ? Number(page.page_index)
        : index + 1;
      const status = String(page.status || '').trim().toLowerCase();
      const degraded =
        DEGRADED_PAGE_STATUSES.has(status) ||
        Boolean(page.low_confidence) ||
        page.text_present === false;
      return {
        pageIndex,
        status: DEGRADED_PAGE_STATUSES.has(status) ? status : degraded ? 'partial' : 'success',
        degraded,
      };
    })
    .filter(Boolean);
  const degraded = normalized.filter((page) => page.degraded);
  const reasons = Array.isArray(trust?.reasons) ? trust.reasons : [];
  const hasPageTrustReason = reasons.some((reason) => PAGE_LEVEL_TRUST_REASONS.has(reason));
  return {
    shouldRender: degraded.length > 0 || (trust?.lowConfidence && hasPageTrustReason),
    degraded,
    totalPages: normalized.length,
  };
}

function getSubmissionTrust(workspacePart, canViewSessionInsights) {
  const markJson = workspacePart?.mark_json || {};
  const trust = markJson?.submission_trust;
  const hasTrustMetadata = Boolean(trust && typeof trust === 'object');
  if (!hasTrustMetadata) {
    return {
      hasTrustMetadata: false,
      interpretedAnswer: null,
      lowConfidence: false,
      message: '',
      categories: [],
      reasons: [],
      primaryCategory: '',
      transcriptionSourceLabel: '',
    };
  }
  const interpretedAnswer = formatTrustValue(trust.interpreted_answer);
  const categories = Array.isArray(trust.categories)
    ? trust.categories.filter(Boolean)
    : [];
  const lowConfidence = Boolean(trust.low_confidence || categories.length);
  const reasons = Array.isArray(trust.reasons)
    ? trust.reasons.filter(Boolean)
    : [];
  const message = lowConfidence ? getSubmissionTrustMessage(trust, categories) : '';
  const normalizedTrust = {
    hasTrustMetadata,
    interpretedAnswer,
    lowConfidence,
    message,
    categories,
    reasons,
    primaryCategory:
      typeof trust.primary_category === 'string' ? trust.primary_category : '',
    transcriptionSourceLabel:
      TYPED_TRANSCRIPTION_SOURCE_LABELS[trust.final_transcription_source] || '',
  };
  return {
    ...normalizedTrust,
    recoveryGuidance: lowConfidence
      ? getSubmissionRecoveryGuidance(normalizedTrust, canViewSessionInsights)
      : null,
  };
}

function getSafeSubmissionRecoveryCopy(detail) {
  const text = String(detail || '').toLowerCase();
  if (!text) {
    return 'The submission could not be processed. Check the image and try again.';
  }

  if (
    text.includes('heic') ||
    text.includes('heif') ||
    text.includes('unsupported_mobile_format')
  ) {
    return 'The HEIC/HEIF photo could not be converted. Retake the photo or upload a JPEG/PNG screenshot or exported image.';
  }

  if (
    text.includes('oversized_upload') ||
    text.includes('too large') ||
    text.includes('maximum allowed size') ||
    text.includes('too many pixels')
  ) {
    return 'The image is too large to process. Upload a smaller JPEG/PNG image or retake the photo closer to the page.';
  }

  if (
    text.includes('unsupported_file_type') ||
    text.includes('unsupported file') ||
    text.includes('not a valid image') ||
    text.includes('invalid image') ||
    text.includes('input_type')
  ) {
    return 'Upload a supported JPEG or PNG image and try again.';
  }

  if (
    text.includes('empty') ||
    text.includes('blank') ||
    text.includes('corrupt') ||
    text.includes('normalization') ||
    text.includes('normalisation') ||
    text.includes('decodable') ||
    text.includes('visible working')
  ) {
    return 'Upload a clearer JPEG/PNG image with visible working.';
  }

  if (
    text.includes(SUBMISSION_PROCESSING_UNAVAILABLE_CODE) ||
    text.includes('ocr') ||
    text.includes('vision') ||
    text.includes('transcription') ||
    text.includes('provider') ||
    text.includes('failed; please retry') ||
    text.includes('timeout')
  ) {
    return SUBMISSION_PROCESSING_UNAVAILABLE_COPY;
  }

  if (text.includes('could not be read')) {
    return SUBMISSION_UNREADABLE_COPY;
  }

  return 'The submission could not be processed. Check the image and try again.';
}

function DegradedPageStateNote({ pageState, canViewSessionInsights }) {
  if (!pageState?.shouldRender) {
    return null;
  }
  const totalPages = pageState.totalPages || pageState.degraded.length;
  const degradedCount = pageState.degraded.length;
  return (
    <div className="submission-page-state-note" aria-label="Page OCR review note">
      <p>
        <strong>
          {degradedCount > 0
            ? `${degradedCount} of ${totalPages || degradedCount} page${(totalPages || degradedCount) === 1 ? '' : 's'} had OCR issues.`
            : 'Some uploaded pages may need OCR review.'}
        </strong>
      </p>
      {degradedCount > 0 ? (
        <ul className="submission-page-state-list">
          {pageState.degraded.slice(0, 3).map((page) => (
            <li key={`${page.pageIndex}-${page.status}`}>
              Page {page.pageIndex} {getPageStateLabel(page.status)}.
            </li>
          ))}
        </ul>
      ) : null}
      <p className="submission-page-state-guidance">
        {canViewSessionInsights
          ? TUTOR_DEGRADED_PAGE_GUIDANCE
          : STUDENT_DEGRADED_PAGE_GUIDANCE}
      </p>
    </div>
  );
}

function SubmissionTrustCard({ trust, pageState, onReviewOverride, canViewSessionInsights }) {
  if (!trust?.hasTrustMetadata) {
    return null;
  }
  const cardClassName = trust.lowConfidence
    ? 'submission-trust-card submission-trust-card-warning'
    : 'submission-trust-card';
  return (
    <div className={cardClassName} data-testid="submission-trust-card">
      {trust.interpretedAnswer ? (
        <p className="submission-trust-answer">
          <span>We interpreted your final answer as:</span>{' '}
          <strong>{trust.interpretedAnswer}</strong>
        </p>
      ) : null}
      {canViewSessionInsights && trust.transcriptionSourceLabel ? (
        <p className="submission-trust-source-badge">
          {trust.transcriptionSourceLabel}
        </p>
      ) : null}
      {trust.lowConfidence ? (
        <p className="submission-trust-warning" role="status">
          {trust.message}
        </p>
      ) : null}
      {trust.lowConfidence ? (
        <div className="submission-trust-recovery">
          <p>
            <strong>{SUBMISSION_TRUST_RECOVERY_HEADING}</strong>
          </p>
          <p>
            {canViewSessionInsights
              ? TUTOR_SUBMISSION_TRUST_RECOVERY_COPY
              : STUDENT_SUBMISSION_TRUST_RECOVERY_COPY}
          </p>
          {trust.recoveryGuidance ? (
            <p className="submission-trust-recovery-guidance">
              {trust.recoveryGuidance.text}
            </p>
          ) : null}
          <DegradedPageStateNote
            pageState={pageState}
            canViewSessionInsights={canViewSessionInsights}
          />
          {onReviewOverride ? (
            <button
              type="button"
              className="btn btn-tertiary submission-trust-recovery-action"
              onClick={onReviewOverride}
            >
              Review override options
            </button>
          ) : null}
        </div>
      ) : null}
      {!trust.interpretedAnswer && !trust.lowConfidence ? (
        <p className="submission-trust-warning">
          We do not have a clear interpreted final answer for this mark.
        </p>
      ) : null}
    </div>
  );
}

function EvidenceConfirmationPanel({
  isConfirmed,
  confirmedLowConfidence,
  isTutorAdjusted,
  isEmphasized,
  canConfirm,
  isConfirming,
  onConfirm,
}) {
  if (!isConfirmed && !canConfirm) {
    return null;
  }
  const panelClassName = isEmphasized
    ? 'evidence-confirmation-panel evidence-confirmation-panel-emphasis'
    : 'evidence-confirmation-panel';
  return (
    <div className={panelClassName}>
      {isConfirmed ? (
        <div className="evidence-confirmation-state">
          <p className="notice notice-info evidence-confirmation-notice">
            Tutor reviewed and confirmed this evidence.
          </p>
          {confirmedLowConfidence ? (
            <p className="notice notice-warn evidence-confirmation-notice">
              Tutor confirmed this evidence after reviewing an originally low-confidence mark.
            </p>
          ) : null}
        </div>
      ) : null}
      {canConfirm && !isConfirmed ? (
        <div className="evidence-confirmation-action">
          <p className="evidence-confirmation-copy">
            {isTutorAdjusted
              ? 'This mark has been adjusted by a tutor. Confirm evidence only if the final reviewed judgment is trustworthy.'
              : 'If you have reviewed the work and the mark is usable, confirm the evidence.'}{' '}
            <strong>This does not change the score.</strong>
          </p>
          <button
            type="button"
            className="btn btn-secondary evidence-confirmation-button"
            onClick={onConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? 'Confirming...' : 'Confirm reviewed evidence'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TutorSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const runtimeSession = useSessionRuntimeSession();
  const { setReportIssueContext } = useReportIssueContext();
  const [restoredDraft] = useState(() => readTutorSessionDraft(sessionId));
  const [question, setQuestion] = useState(() => restoredDraft?.question || null);
  const [studentDisplayName, setStudentDisplayName] = useState('');
  const [attemptId, setAttemptId] = useState(() => restoredDraft?.attemptId || '');
  const [isLoading, setIsLoading] = useState(() => !restoredDraft);
  const [isSkipping, setIsSkipping] = useState(false);
  const [isExhausted, setIsExhausted] = useState(false);
  const [isSessionEnded, setIsSessionEnded] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [partErrors, setPartErrors] = useState({});
  const [isSubmittingPart, setIsSubmittingPart] = useState({});
  const [isMarkingPart, setIsMarkingPart] = useState({});
  const [isSkippingPart, setIsSkippingPart] = useState({});
  const [isOverridingPart, setIsOverridingPart] = useState({});
  const [isConfirmingPart, setIsConfirmingPart] = useState({});
  const [isConfirmingTranscription, setIsConfirmingTranscription] = useState({});
  const [isEditingTranscription, setIsEditingTranscription] = useState({});
  const [transcriptionDraftByPart, setTranscriptionDraftByPart] = useState(
    () => restoredDraft?.transcriptionDraftByPart || {}
  );
  const [transcriptionNoticeByPart, setTranscriptionNoticeByPart] = useState({});
  const [submittedTranscriptionByPart, setSubmittedTranscriptionByPart] = useState({});
  const [overrideDraftByPart, setOverrideDraftByPart] = useState(
    () => restoredDraft?.overrideDraftByPart || {}
  );
  const [overrideDraftSaveStatus, setOverrideDraftSaveStatus] = useState('idle');
  const [filesByPart, setFilesByPart] = useState(() => restoredDraft?.filesByPart || {});
  const [serializedFilesByPart, setSerializedFilesByPart] = useState(
    () => restoredDraft?.serializedFilesByPart || {}
  );
  const [inputTypeByPart, setInputTypeByPart] = useState(
    () => restoredDraft?.inputTypeByPart || {}
  );
  const [typedAnswerByPart, setTypedAnswerByPart] = useState(
    () => restoredDraft?.typedAnswerByPart || {}
  );
  const [drawingDraftByPart, setDrawingDraftByPart] = useState(
    () => restoredDraft?.drawingDraftByPart || {}
  );
  const [attemptWorkspace, setAttemptWorkspace] = useState(
    () => restoredDraft?.attemptWorkspace || null
  );
  const [finalizeTelemetry, setFinalizeTelemetry] = useState(null);
  const [devForceCorrect, setDevForceCorrect] = useState(false);
  const feedbackPollerRef = useRef(null);
  const sessionDraftWriteTimeoutRef = useRef(null);
  const latestSessionDraftRef = useRef(null);
  const isSessionDraftMountedRef = useRef(true);
  const filesSerializationVersionRef = useRef(0);
  const hasMountedFilesPersistenceRef = useRef(false);
  const overrideSectionRefs = useRef({});
  const overrideFirstInputRefs = useRef({});
  const pollMetaRef = useRef({
    attemptId: null,
    tries: 0,
    delayMs: 1000,
    lastSig: '',
    startedAtMs: 0,
  });
  const autosaveDraftTimeoutRef = useRef(null);
  const draftLoadedAttemptRef = useRef(restoredDraft?.attemptId ? String(restoredDraft.attemptId) : '');
  const isRestoringDraftRef = useRef(false);
  const lastSavedDraftKeyRef = useRef(
    restoredDraft?.overrideDraftByPart ? JSON.stringify(restoredDraft.overrideDraftByPart) : ''
  );
  const currentDraftKeyRef = useRef(
    restoredDraft?.overrideDraftByPart ? JSON.stringify(restoredDraft.overrideDraftByPart) : ''
  );
  const currentAttemptIdRef = useRef(
    restoredDraft?.attemptId ? String(restoredDraft.attemptId) : ''
  );
  const hasOverrideDraftUserEditRef = useRef(false);
  const user = getStoredUser();
  const isParent = user?.role === 'PARENT';
  const isStudent = user?.role === 'STUDENT';
  const isAdmin = user?.role === 'ADMIN';
  const canViewSessionInsights = user?.role === 'ADMIN' || user?.role === 'TUTOR';
  const canManageOverrides = user?.role === 'ADMIN' || user?.role === 'TUTOR';
  const canShowDebugUI = shouldShowDebugUI();
  const [showWhy, setShowWhy] = useState(false);
  const [showWorkedExample, setShowWorkedExample] = useState(false);
  const [tutorSupport, setTutorSupport] = useState(() => restoredDraft?.tutorSupport || null);
  const [selectionMeta, setSelectionMeta] = useState(() => restoredDraft?.selectionMeta || null);
  const [masterySelection, setMasterySelection] = useState(
    () => restoredDraft?.masterySelection || null
  );
  const [masteryInspect, setMasteryInspect] = useState(null);
  const [isInspectLoading, setIsInspectLoading] = useState(false);
  const [inspectError, setInspectError] = useState('');
  const [pendingNext, setPendingNext] = useState(null);
  const [banner, setBanner] = useState(null);
  const [eventLog, setEventLog] = useState([]);
  const [feedbackPollStatus, setFeedbackPollStatus] = useState({
    attemptId: '',
    stale: false,
    pollFailed: false,
  });

  const debugLog = useCallback((...args) => {
    if (canShowDebugUI) {
      console.debug(...args);
    }
  }, [canShowDebugUI]);

  useEffect(() => {
    setReportIssueContext(buildTutorReportContext({
      sessionId,
      attemptId,
      question,
      masterySelection,
      selectionMeta,
    }));
    return () => setReportIssueContext({});
  }, [
    attemptId,
    masterySelection,
    question,
    selectionMeta,
    sessionId,
    setReportIssueContext,
  ]);

  useEffect(() => {
    if (!sessionId || !canViewSessionInsights) {
      setStudentDisplayName('');
      return;
    }

    let cancelled = false;
    const loadSessionStudent = async () => {
      try {
        const response = await getSession(sessionId);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (!cancelled) {
          setStudentDisplayName(String(data?.student_display_name || '').trim());
        }
      } catch (_error) {
        // Student attribution is supplementary; the question workflow remains usable.
      }
    };
    loadSessionStudent();

    return () => {
      cancelled = true;
    };
  }, [canViewSessionInsights, sessionId]);

  const handleReviewOverrideOptions = useCallback((partLabel) => {
    const section = overrideSectionRefs.current?.[partLabel];
    if (!section) {
      return;
    }
    section.open = true;
    section.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    const scheduleFocus =
      window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    scheduleFocus(() => {
      const firstInput = overrideFirstInputRefs.current?.[partLabel];
      if (firstInput && typeof firstInput.focus === 'function') {
        firstInput.focus();
      } else if (typeof section.focus === 'function') {
        section.focus();
      }
    });
  }, []);
  const [showTutorGuidance, setShowTutorGuidance] = useState(false);

  const buildPartsPollSignature = useCallback((workspaceState) => {
    return JSON.stringify(
      (workspaceState?.parts || []).map((part) => ({
        id: part?.part_attempt_id ?? null,
        s: part?.status ?? null,
        m: part?.marked_at ?? null,
        t: part?.mark_json?.total?.score ?? null,
        x: part?.mark_json?.total?.max ?? null,
        f: (part?.feedback_markdown || '').length,
      }))
    );
  }, []);

  const pushEvent = useCallback(({ name, status, detail }) => {
    setEventLog((prev) => {
      const next = [
        {
          timestamp: nowIso(),
          name: name || 'unknown',
          status: status || 'unknown',
          detail: detail || '',
        },
        ...prev,
      ];
      return next.slice(0, 20);
    });
  }, []);

  const buildErrorMessage = useCallback(async (response, fallback) => {
    const status = response?.status;
    let detail = '';

    if (response) {
      try {
        const data = await response.json();
        detail = data?.detail || data?.message || '';
      } catch (error) {
        detail = '';
      }
    }

    if (detail || status) {
      debugLog('ErrorDetail:', { status: status ?? 'unknown', detail, fallback });
    }
    if (status === 401) {
      return 'Your session has expired. Please sign in again.';
    }
    if (status === 403 && detail === 'Forbidden part-attempt access') {
      return `${fallback} You do not have access to this answer attempt. Reload the session and try again.`;
    }
    const detailSuffix = detail ? ` - ${detail}` : '';
    return `${fallback} (status ${status ?? 'unknown'})${detailSuffix}`;
  }, [debugLog]);

  const buildSubmissionErrorMessage = useCallback(async (response, fallback) => {
    const status = response?.status;
    let detail = '';

    if (response) {
      try {
        const data = await response.json();
        detail = data?.detail || data?.message || '';
      } catch (error) {
        detail = '';
      }
    }

    if (status === 401) {
      return 'Your session has expired. Please sign in again.';
    }
    if (status === 403 && detail === 'Forbidden part-attempt access') {
      return `${fallback} You do not have access to this answer attempt. Reload the session and try again.`;
    }
    if (status === 500) {
      return 'Something went wrong on our side. Try again.';
    }

    return `${fallback} ${getSafeSubmissionRecoveryCopy(detail)}`;
  }, []);

  const isGuardianVerificationBlocked = useCallback(async (response) => {
    if (response?.status !== 403) {
      return false;
    }
    let data = null;
    try {
      const readable = typeof response.clone === 'function' ? response.clone() : response;
      data = await readable.json();
    } catch (_error) {
      data = null;
    }
    if (data?.detail === 'guardian_verification_required') {
      navigate('/access-restricted?reason=guardian', { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const loadMasteryInspect = useCallback(async () => {
    if (!canViewSessionInsights) {
      return;
    }
    if (!sessionId) {
      return;
    }

    setIsInspectLoading(true);
    setInspectError('');

    try {
      const response = await getMasteryInspectWithFallback(sessionId, {
        allowDebugFallback: canShowDebugUI,
      });
      if (!response.ok) {
        const message = await buildErrorMessage(
          response,
          'Failed to load mastery inspect.'
        );
        setInspectError(message);
        setMasteryInspect(null);
        return;
      }

      const data = await response.json();
      setMasteryInspect(data ?? null);
    } catch (error) {
      setInspectError(error.message || 'Failed to load mastery inspect.');
      setMasteryInspect(null);
    } finally {
      setIsInspectLoading(false);
    }
  }, [buildErrorMessage, canShowDebugUI, canViewSessionInsights, sessionId]);

  const resetPerPartState = useCallback(() => {
    filesSerializationVersionRef.current += 1;
    latestSessionDraftRef.current = null;
    try {
      sessionStorage.removeItem(getTutorSessionDraftKey(sessionId));
    } catch (_error) {
      // Storage may be unavailable; resetting the live workspace must still succeed.
    }
    setFilesByPart({});
    setSerializedFilesByPart({});
    setInputTypeByPart({});
    setTypedAnswerByPart({});
    setDrawingDraftByPart({});
    setPartErrors({});
    setIsSubmittingPart({});
    setIsMarkingPart({});
    setIsOverridingPart({});
    setIsConfirmingPart({});
    setIsConfirmingTranscription({});
    setIsEditingTranscription({});
    setTranscriptionDraftByPart({});
    setTranscriptionNoticeByPart({});
    setSubmittedTranscriptionByPart({});
    setOverrideDraftByPart({});
    setOverrideDraftSaveStatus('idle');
    setAttemptWorkspace(null);
    draftLoadedAttemptRef.current = '';
    lastSavedDraftKeyRef.current = '';
    currentDraftKeyRef.current = '';
    currentAttemptIdRef.current = '';
    hasOverrideDraftUserEditRef.current = false;
    // sessionId keys the sessionStorage draft this clears, so the callbacks
    // that call this must re-bind when it changes (react-hooks/exhaustive-deps).
  }, [sessionId]);

  const isOverrideDraftMapEmpty = useCallback((draftMap) => {
    if (!draftMap || typeof draftMap !== 'object') {
      return true;
    }
    return Object.keys(draftMap).length === 0;
  }, []);

  const hasVolatileSelectedFiles = useMemo(
    () =>
      Object.values(filesByPart).some(
        (files) => Array.isArray(files) && files.filter(Boolean).length > 0
      ),
    [filesByPart]
  );

  const hasUnsavedOverrideDraft =
    overrideDraftSaveStatus === 'dirty' ||
    overrideDraftSaveStatus === 'saving' ||
    overrideDraftSaveStatus === 'failed';
  const hasVolatileDrawingDraft = useMemo(
    () => Object.values(drawingDraftByPart).some(
      (pages) => Array.isArray(pages) && pages.some(
        (paths) => Array.isArray(paths) && paths.length > 0
      )
    ),
    [drawingDraftByPart]
  );
  const hasVolatileTypedAnswer = useMemo(
    () => Object.values(typedAnswerByPart).some((answer) => String(answer || '').trim()),
    [typedAnswerByPart]
  );
  const hasVolatileTutorWork =
    hasUnsavedOverrideDraft ||
    hasVolatileSelectedFiles ||
    hasVolatileDrawingDraft ||
    hasVolatileTypedAnswer;

  const confirmLeaveWithVolatileWork = useCallback(() => {
    if (!hasVolatileTutorWork) {
      return true;
    }
    return window.confirm(DRAFT_LEAVE_WARNING);
  }, [hasVolatileTutorWork]);

  const fetchAttemptState = useCallback(
    async (attemptIdToLoad, { fromPoll = false } = {}) => {
      if (!attemptIdToLoad) {
        setAttemptWorkspace(null);
        return null;
      }

      try {
        const response = await getAttemptState(attemptIdToLoad);
        if (await isGuardianVerificationBlocked(response)) {
          return null;
        }

        if (!response.ok) {
          const message = await buildErrorMessage(
            response,
            'Failed to load attempt state.'
          );
          throw new Error(message);
        }

        const data = await response.json();
        const dataOrNull = data ?? null;
        const nextSig = buildPartsPollSignature(dataOrNull);
        if (fromPoll) {
          const isUnchanged = nextSig === pollMetaRef.current.lastSig;
          if (isUnchanged) {
            return { data: dataOrNull, changed: false, failed: false };
          }
          pollMetaRef.current.lastSig = nextSig;
          pollMetaRef.current.tries = 0;
          pollMetaRef.current.delayMs = 1000;
        } else {
          pollMetaRef.current.lastSig = nextSig;
        }

        setAttemptWorkspace(dataOrNull);
        const partsSummary =
          dataOrNull?.parts?.map((part) => ({
            part_id: part.part_id,
            status: part.status,
          })) ?? [];
        debugLog('TutorState:', { attemptId: attemptIdToLoad, parts: partsSummary });
        pushEvent({
          name: 'fetchAttemptState',
          status: 'success',
          detail: `attempt_id=${attemptIdToLoad} parts=${partsSummary.length}`,
        });
        if (fromPoll) {
          return { data: dataOrNull, changed: true, failed: false };
        }
        return dataOrNull;
      } catch (error) {
        if (fromPoll) {
          debugLog('TutorStatePoll error', {
            attemptId: attemptIdToLoad,
            error: error.message || 'unknown',
          });
          return { data: null, changed: false, failed: true };
        }
        setErrorMessage(error.message || 'Failed to load attempt state.');
        pushEvent({
          name: 'fetchAttemptState',
          status: 'fail',
          detail: `attempt_id=${attemptIdToLoad} error=${error.message || 'unknown'}`,
        });
        return null;
      }
    },
    [buildErrorMessage, buildPartsPollSignature, debugLog, isGuardianVerificationBlocked, pushEvent]
  );

  const loadNextQuestion = useCallback(async ({ progressionDebugOverride = false, preserveBanner = false } = {}) => {
    setIsLoading(true);
    setErrorMessage('');
    setInfoMessage('');
    setFinalizeTelemetry(null);
    setPartErrors({});
    setPendingNext(null);
    setShowWorkedExample(false);
    if (!preserveBanner) {
      setBanner(null);
    }

    const useMastery = !progressionDebugOverride;

    try {
      const response = await postNextQuestion(sessionId, {
        mastery: useMastery,
        createAttempt: true,
      });
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }

      if (response.status === 404) {
        setIsExhausted(true);
        setQuestion(null);
        setAttemptId('');
        setMasterySelection(null);
        setSelectionMeta(null);
        setTutorSupport(null);
        setShowWhy(false);
        resetPerPartState();
        pushEvent({
          name: 'loadNextQuestion',
          status: 'success',
          detail: 'exhausted=true',
        });
        return;
      }

      if (response.status === 409) {
        setIsSessionEnded(true);
        setQuestion(null);
        setAttemptId('');
        setMasterySelection(null);
        setSelectionMeta(null);
        setTutorSupport(null);
        setShowWhy(false);
        resetPerPartState();
        return;
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to load the next question.');
        throw new Error(message);
      }

      const data = await response.json();
      setIsSessionEnded(false);
      setIsExhausted(false);
      const nextQuestion = data?.question ?? null;
      const nextAttemptId = data?.attempt_id ?? '';
      setQuestion(nextQuestion);
      setAttemptId(nextAttemptId);
      const selection = normalizeMasterySelection(data?.selection);
      setTutorSupport(data?.tutor_support ?? null);
      setSelectionMeta(selection);
      setMasterySelection(selection);
      setShowWhy(false);
      setShowWorkedExample(false);
      resetPerPartState();
      if (canViewSessionInsights) {
        void loadMasteryInspect();
      }
      pushEvent({
        name: 'loadNextQuestion',
        status: 'success',
        detail: `question_id=${nextQuestion?.id || 'n/a'} attempt_id=${nextAttemptId || 'n/a'} reason=${
          data?.selection?.reason || 'n/a'
        }`,
      });
      await fetchAttemptState(nextAttemptId);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load the next question.');
      pushEvent({
        name: 'loadNextQuestion',
        status: 'fail',
        detail: error.message || 'Failed to load the next question.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    buildErrorMessage,
    canViewSessionInsights,
    fetchAttemptState,
    isGuardianVerificationBlocked,
    loadMasteryInspect,
    pushEvent,
    resetPerPartState,
    sessionId,
  ]);

  /**
   * Resume the session's open attempt from the SERVER before asking for a next
   * question.
   *
   * The sessionStorage draft is keyed per tab and dies with the tab, so it can
   * never be the only recovery source: an ACTIVE session must survive a closed
   * tab, a closed browser and a return the next day
   * (docs/01_product/session_lifecycle.md:577-581). GET /sessions/:id/state
   * reports the open attempt; GET /attempts/:id returns it with its question.
   * Neither creates an attempt, so a resume can never produce a duplicate.
   * Only when the server reports no open attempt do we ask for the next one.
   */
  const resumeOpenAttemptOrLoadNext = useCallback(async () => {
    setIsLoading(true);
    try {
      const stateResponse = await getSessionState(sessionId);
      if (!stateResponse?.ok) {
        await loadNextQuestion();
        return;
      }
      const state = await stateResponse.json();
      const openAttemptId = state?.active_attempt_id ?? null;
      if (!openAttemptId) {
        await loadNextQuestion();
        return;
      }

      const attemptResponse = await getAttempt(openAttemptId);
      if (!attemptResponse?.ok) {
        await loadNextQuestion();
        return;
      }
      const openAttempt = await attemptResponse.json();
      const openQuestion = openAttempt?.question ?? null;
      if (!openQuestion) {
        await loadNextQuestion();
        return;
      }

      setErrorMessage('');
      setInfoMessage('');
      setIsSessionEnded(false);
      setIsExhausted(false);
      setQuestion(openQuestion);
      setAttemptId(openAttemptId);
      setShowWhy(false);
      setShowWorkedExample(false);
      resetPerPartState();
      pushEvent({
        name: 'resumeOpenAttempt',
        status: 'success',
        detail: `question_id=${openQuestion?.id || 'n/a'} attempt_id=${openAttemptId}`,
      });
      if (canViewSessionInsights) {
        void loadMasteryInspect();
      }
      await fetchAttemptState(openAttemptId);
    } catch (error) {
      // A failed restore must not strand the learner on an empty screen.
      await loadNextQuestion();
    } finally {
      setIsLoading(false);
    }
  }, [
    canViewSessionInsights,
    fetchAttemptState,
    loadMasteryInspect,
    loadNextQuestion,
    pushEvent,
    resetPerPartState,
    sessionId,
  ]);

  const handleSkipQuestion = useCallback(async () => {
    if (!attemptId) {
      setErrorMessage('No active attempt to skip.');
      return;
    }
    if (!confirmLeaveWithVolatileWork()) {
      return;
    }

    setIsSkipping(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const abandonResponse = await postAbandonAttempt(attemptId);
      if (await isGuardianVerificationBlocked(abandonResponse)) {
        return;
      }

      if (!abandonResponse.ok) {
        const message = await buildErrorMessage(
          abandonResponse,
          'Failed to abandon the current attempt.'
        );
        throw new Error(message);
      }

      pushEvent({
        name: 'abandonAttempt',
        status: 'success',
        detail: `attempt_id=${attemptId}`,
      });
      await loadNextQuestion();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to abandon the current attempt.');
      pushEvent({
        name: 'abandonAttempt',
        status: 'fail',
        detail: `attempt_id=${attemptId || 'n/a'} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsSkipping(false);
    }
  }, [
    attemptId,
    buildErrorMessage,
    confirmLeaveWithVolatileWork,
    isGuardianVerificationBlocked,
    loadNextQuestion,
    pushEvent,
  ]);

  const handleSubmitPart = async (partLabel) => {
    if (isSubmittingPart[partLabel]) {
      return;
    }
    if (!attemptId) {
      setPartErrors((prev) => ({ ...prev, [partLabel]: 'No active attempt.' }));
      pushEvent({
        name: 'submitPart',
        status: 'fail',
        detail: `part=${partLabel} error=no active attempt`,
      });
      return;
    }

    setPartErrors((prev) => ({ ...prev, [partLabel]: '' }));

    try {
      const selectedFiles = Array.isArray(filesByPart[partLabel])
        ? filesByPart[partLabel].filter(Boolean)
        : [];
      const typedAnswer = String(typedAnswerByPart[partLabel] || '').trim();
      if (!selectedFiles.length && !typedAnswer) {
        setPartErrors((prev) => ({
          ...prev,
          [partLabel]: 'Type an answer or upload an image to submit.',
        }));
        pushEvent({
          name: 'submitPart',
          status: 'fail',
          detail: `part=${partLabel} error=no files selected`,
        });
        return;
      }
      const selectedInputType = typedAnswer ? 'TEXT' : inputTypeByPart[partLabel];
      if (!typedAnswer && !selectedInputType) {
        setPartErrors((prev) => ({
          ...prev,
          [partLabel]: 'Choose upload photo or draw answer first.',
        }));
        pushEvent({
          name: 'submitPart',
          status: 'fail',
          detail: `part=${partLabel} error=missing input_type`,
        });
        return;
      }
      const questionPart = question?.parts?.find((part) => part.part_label === partLabel);
      const workspacePart =
        attemptWorkspace?.parts?.find((part) => part.part_id === questionPart?.id) ?? null;
      const isMarked = workspacePart?.status === 'MARKED' || !!workspacePart?.marked_at;
      if (isMarked) {
        setPartErrors((prev) => ({
          ...prev,
          [partLabel]: 'This part is already marked and cannot be replaced.',
        }));
        return;
      }
      const formData = typedAnswer
        ? buildTextSubmissionFormData({ answerText: typedAnswer })
        : buildImageSubmissionFormData({
            files: selectedFiles,
            inputType: selectedInputType,
          });

      setIsSubmittingPart((prev) => ({ ...prev, [partLabel]: true }));
      const response = await postSubmitPart(attemptId, partLabel, formData);
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }

      if (!response.ok) {
        const message = await buildSubmissionErrorMessage(response, 'Failed to submit part.');
        throw new Error(message);
      }

      const data = await response.json();
      if (data?.part_attempt_id || data?.id) {
        if (typeof data?.transcription?.latex === 'string') {
          setSubmittedTranscriptionByPart((prev) => ({
            ...prev,
            [partLabel]: data.transcription.latex,
          }));
        }
        debugLog('PartSubmit:', { attemptId, partLabel, inputType: selectedInputType });
        pushEvent({
          name: 'submitPart',
          status: 'success',
          detail: `attempt_id=${attemptId} part=${partLabel} input_type=${selectedInputType} part_attempt_id=${
            data?.part_attempt_id || data?.id || 'n/a'
          }`,
        });
        setFilesByPart((prev) => ({ ...prev, [partLabel]: [] }));
        setInputTypeByPart((prev) => ({ ...prev, [partLabel]: null }));
        setTypedAnswerByPart((prev) => ({ ...prev, [partLabel]: '' }));
        await fetchAttemptState(attemptId);
      }
    } catch (error) {
      const message =
        error?.message === 'Failed to fetch'
          ? 'Something went wrong on our side. Try again.'
          : error?.message || 'Submit failed.';
      setPartErrors((prev) => ({ ...prev, [partLabel]: message }));
      pushEvent({
        name: 'submitPart',
        status: 'fail',
        detail: `attempt_id=${attemptId} part=${partLabel} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsSubmittingPart((prev) => ({ ...prev, [partLabel]: false }));
    }
  };

  const handleMarkPart = async (partLabel) => {
    if (isMarkingPart[partLabel]) {
      return;
    }
    const questionPart = question?.parts?.find((part) => part.part_label === partLabel);
    const partId = questionPart?.id;
    const workspacePart =
      attemptWorkspace?.parts?.find((part) => part.part_id === partId) ?? null;
    const partAttemptId = workspacePart?.part_attempt_id;
    if (!partAttemptId) {
      setPartErrors((prev) => ({ ...prev, [partLabel]: 'No submitted part attempt.' }));
      pushEvent({
        name: 'markPart',
        status: 'fail',
        detail: `part=${partLabel} error=no submitted part attempt`,
      });
      return;
    }

    setPartErrors((prev) => ({ ...prev, [partLabel]: '' }));
    setIsMarkingPart((prev) => ({ ...prev, [partLabel]: true }));

    try {
      const response = await postMarkPartAttempt(partAttemptId, {
        forceCorrect: canShowDebugUI ? devForceCorrect : false,
      });
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to mark part.');
        throw new Error(message);
      }

      await response.json().catch(() => ({}));
      debugLog('PartMark:', { attemptId, partLabel, partAttemptId });
      pushEvent({
        name: 'markPart',
        status: 'success',
        detail: `attempt_id=${attemptId} part=${partLabel} part_attempt_id=${partAttemptId}`,
      });
      await fetchAttemptState(attemptId);
    } catch (error) {
      setPartErrors((prev) => ({ ...prev, [partLabel]: error.message || 'Mark failed.' }));
      pushEvent({
        name: 'markPart',
        status: 'fail',
        detail: `attempt_id=${attemptId} part=${partLabel} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsMarkingPart((prev) => ({ ...prev, [partLabel]: false }));
    }
  };

  const handleFixTranscription = (partLabel, rawTranscription) => {
    setTranscriptionDraftByPart((prev) => ({
      ...prev,
      [partLabel]: rawTranscription,
    }));
    setTranscriptionNoticeByPart((prev) => ({ ...prev, [partLabel]: '' }));
    setIsEditingTranscription((prev) => ({ ...prev, [partLabel]: true }));
  };

  const handleConfirmTranscriptionAndMark = async (partLabel, partAttemptId) => {
    const finalTranscription = transcriptionDraftByPart[partLabel] ?? '';
    if (!finalTranscription.trim()) {
      setTranscriptionNoticeByPart((prev) => ({
        ...prev,
        [partLabel]:
          'A correction cannot be empty because the original reading would be used for marking. Replace the submission, or cancel and mark the original reading.',
      }));
      return;
    }
    setIsConfirmingTranscription((prev) => ({ ...prev, [partLabel]: true }));
    setTranscriptionNoticeByPart((prev) => ({ ...prev, [partLabel]: '' }));

    try {
      const response = await patchConfirmPartAttemptTranscription(partAttemptId, {
        transcription_latex_final: finalTranscription,
      });
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }
      if (!response.ok) {
        throw new Error('The correction could not be saved. Your work will still be marked.');
      }
      await response.json().catch(() => ({}));
      setIsEditingTranscription((prev) => ({ ...prev, [partLabel]: false }));
    } catch (_error) {
      setTranscriptionNoticeByPart((prev) => ({
        ...prev,
        [partLabel]: 'The correction could not be saved. Your work will still be marked.',
      }));
    } finally {
      setIsConfirmingTranscription((prev) => ({ ...prev, [partLabel]: false }));
    }

    await handleMarkPart(partLabel);
  };

  const handleSkipPart = async (partLabel) => {
    const questionPart = question?.parts?.find((part) => part.part_label === partLabel);
    const partId = questionPart?.id;
    const workspacePart =
      attemptWorkspace?.parts?.find((part) => part.part_id === partId) ?? null;
    const partAttemptId = workspacePart?.part_attempt_id;
    const partStatus = workspacePart?.status ?? 'NOT_STARTED';
    const isSubmitted = partStatus === 'SUBMITTED';
    const isMarked = getPartTotals(workspacePart).isMarked;

    if (!isSubmitted && !isMarked) {
      const confirmed = window.confirm(`Leave part ${partLabel} blank and score 0?`);
      if (!confirmed) {
        return;
      }
    }

    setIsSkippingPart((prev) => ({ ...prev, [partLabel]: true }));
    setPartErrors((prev) => ({ ...prev, [partLabel]: '' }));

    try {
      const payload = { reason: 'student_dont_know' };
      const response = partAttemptId
        ? await postSkipPartAttempt(partAttemptId, payload)
        : await postSkipPartByLabel(attemptId, partLabel, payload);
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to skip part.');
        setPartErrors((prev) => ({ ...prev, [partLabel]: message }));
        pushEvent({
          name: 'skipPart',
          status: 'fail',
          detail: `attempt_id=${attemptId} part=${partLabel} error=${message}`,
        });
        return;
      }

      await response.json().catch(() => ({}));
      pushEvent({
        name: 'skipPart',
        status: 'success',
        detail: `attempt_id=${attemptId} part=${partLabel} part_attempt_id=${
          partAttemptId || 'n/a'
        }`,
      });
      await fetchAttemptState(attemptId);
    } catch (error) {
      setPartErrors((prev) => ({ ...prev, [partLabel]: error.message || 'Skip failed.' }));
      pushEvent({
        name: 'skipPart',
        status: 'fail',
        detail: `attempt_id=${attemptId} part=${partLabel} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsSkippingPart((prev) => ({ ...prev, [partLabel]: false }));
    }
  };

  const handleOverridePartChange = useCallback((partLabel, field, value) => {
    hasOverrideDraftUserEditRef.current = true;
    setOverrideDraftByPart((prev) => ({
      ...prev,
      [partLabel]: {
        ...(prev[partLabel] || {}),
        [field]: value,
      },
    }));
  }, []);

  const parseOverrideScore = (value, maxValue, label) => {
    if (maxValue === 0) {
      return 0;
    }
    if (value === '' || value === null || value === undefined) {
      throw new Error(`${label} is required.`);
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      throw new Error(`${label} must be a number.`);
    }
    if (numericValue < 0 || numericValue > maxValue) {
      throw new Error(`${label} must be between 0 and ${maxValue}.`);
    }
    return numericValue;
  };

  const handleSaveOverride = async (partLabel) => {
    const questionPart = question?.parts?.find((part) => part.part_label === partLabel);
    const partId = questionPart?.id;
    const workspacePart =
      attemptWorkspace?.parts?.find((part) => part.part_id === partId) ?? null;
    const partAttemptId = workspacePart?.part_attempt_id;
    if (!partAttemptId) {
      setPartErrors((prev) => ({ ...prev, [partLabel]: 'No part attempt available to override.' }));
      return;
    }

    const { answerMax, methodMax, communicationMax } = getPartRubricMaxes(
      questionPart,
      workspacePart
    );
    const draft = overrideDraftByPart[partLabel] || {};

    setIsOverridingPart((prev) => ({ ...prev, [partLabel]: true }));
    setPartErrors((prev) => ({ ...prev, [partLabel]: '' }));

    try {
      const payload = {
        answer_score: parseOverrideScore(draft.answer_score, answerMax, 'Answer score'),
        method_score: parseOverrideScore(draft.method_score, methodMax, 'Method score'),
        communication_score: parseOverrideScore(
          draft.communication_score,
          communicationMax,
          'Communication score'
        ),
        reason: 'manual',
        tutor_note: draft.tutor_note || '',
      };

      const response = await postOverridePartAttempt(partAttemptId, payload);
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }
      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to save override.');
        if (response.status === 403) {
          if (autosaveDraftTimeoutRef.current) {
            clearTimeout(autosaveDraftTimeoutRef.current);
            autosaveDraftTimeoutRef.current = null;
          }
          hasOverrideDraftUserEditRef.current = false;
          setOverrideDraftSaveStatus('permission_denied');
        }
        throw new Error(message);
      }

      await response.json().catch(() => ({}));
      const draftKey = JSON.stringify(overrideDraftByPart);
      lastSavedDraftKeyRef.current = draftKey;
      currentDraftKeyRef.current = draftKey;
      hasOverrideDraftUserEditRef.current = false;
      setOverrideDraftSaveStatus('saved');
      pushEvent({
        name: 'overridePart',
        status: 'success',
        detail: `attempt_id=${attemptId} part=${partLabel} part_attempt_id=${partAttemptId}`,
      });
      await fetchAttemptState(attemptId);
    } catch (error) {
      setPartErrors((prev) => ({ ...prev, [partLabel]: error.message || 'Override failed.' }));
      pushEvent({
        name: 'overridePart',
        status: 'fail',
        detail: `attempt_id=${attemptId} part=${partLabel} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsOverridingPart((prev) => ({ ...prev, [partLabel]: false }));
    }
  };

  const handleConfirmEvidence = async (partLabel) => {
    const questionPart = question?.parts?.find((part) => part.part_label === partLabel);
    const partId = questionPart?.id;
    const workspacePart =
      attemptWorkspace?.parts?.find((part) => part.part_id === partId) ?? null;
    const partAttemptId = workspacePart?.part_attempt_id;
    if (!partAttemptId) {
      setPartErrors((prev) => ({
        ...prev,
        [partLabel]: 'No marked part attempt available to confirm.',
      }));
      return;
    }

    setIsConfirmingPart((prev) => ({ ...prev, [partLabel]: true }));
    setPartErrors((prev) => ({ ...prev, [partLabel]: '' }));
    setInfoMessage('');

    try {
      const response = await postConfirmPartAttemptEvidence(partAttemptId, {});
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }
      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to confirm reviewed evidence.');
        throw new Error(message);
      }

      await response.json().catch(() => ({}));
      pushEvent({
        name: 'confirmEvidence',
        status: 'success',
        detail: `attempt_id=${attemptId} part=${partLabel} part_attempt_id=${partAttemptId}`,
      });
      await fetchAttemptState(attemptId);
      setInfoMessage('Evidence confirmed. This does not change the score.');
    } catch (error) {
      setPartErrors((prev) => ({
        ...prev,
        [partLabel]: error.message || 'Failed to confirm reviewed evidence.',
      }));
      pushEvent({
        name: 'confirmEvidence',
        status: 'fail',
        detail: `attempt_id=${attemptId} part=${partLabel} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsConfirmingPart((prev) => ({ ...prev, [partLabel]: false }));
    }
  };

  const allPartsMarked = useMemo(() => {
    if (!question?.parts?.length) {
      return false;
    }
    return question.parts.every((part) => {
      const partState =
        attemptWorkspace?.parts?.find((entry) => entry.part_id === part.id) ?? null;
      return getPartTotals(partState).isMarked;
    });
  }, [attemptWorkspace, question]);

  const handleFinalizeAttempt = async () => {
    if (isFinalizing || !attemptId || !question || !allPartsMarked) {
      return;
    }
    if (!confirmLeaveWithVolatileWork()) {
      return;
    }

    if (!attemptId) {
      setErrorMessage('No active attempt to finalize.');
      return;
    }

    if (!allPartsMarked) {
      setErrorMessage('All parts must be marked before finalizing.');
      return;
    }

    setIsFinalizing(true);
    setErrorMessage('');
    setInfoMessage('');
    debugLog('FinalizeAttempt:', { attemptId });

    try {
      const response = await postFinalizeAttempt(attemptId);
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }

      if (response.status === 409) {
        const message = await buildErrorMessage(response, 'Failed to finalize attempt.');
        throw new Error(message);
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to finalize attempt.');
        throw new Error(message);
      }

      const data = await response.json().catch(() => ({}));
      const telemetryFromFinalize =
        data?.telemetry ||
        data?.attempt_telemetry ||
        data?.attempt_signals ||
        null;
      setFinalizeTelemetry(telemetryFromFinalize);
      const nextAction = data?.next_action;
      const nextActionType = nextAction?.action;
      setPendingNext({
        type: nextActionType || 'NEXT_QUESTION',
        attemptId: nextAction?.next_attempt_id || null,
        question: null,
        selection: null,
        tutorSupport: null,
        createdAtIso: nowIso(),
      });
      if (nextActionType === 'SIMILAR_QUESTION') {
        setInfoMessage('Similar question generated');
      } else {
        setInfoMessage('Ready for the next question');
      }
      debugLog('FinalizeAttempt:', { attemptId, status: 'success', nextActionType });
      pushEvent({
        name: 'finalizeAttempt',
        status: 'success',
        detail: `attempt_id=${attemptId} next_action_type=${nextActionType || 'n/a'} next_attempt_id=${
          nextAction?.next_attempt_id || 'n/a'
        }`,
      });
      if (canShowDebugUI) {
        void loadMasteryInspect();
      }

    } catch (error) {
      setErrorMessage(error.message || 'Failed to finalize attempt.');
      pushEvent({
        name: 'finalizeAttempt',
        status: 'fail',
        detail: `attempt_id=${attemptId || 'n/a'} error=${error.message || 'unknown'}`,
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleContinueAfterFinalize = useCallback(async () => {
    if (!pendingNext) {
      return;
    }

    if (pendingNext?.type === 'SIMILAR_QUESTION') {
      setBanner({ type: 'SIMILAR', text: 'Reinforcement question' });
    }

    if (pendingNext?.attemptId && pendingNext?.question) {
      setQuestion(pendingNext.question);
      setAttemptId(pendingNext.attemptId);
      setIsExhausted(false);
      setSelectionMeta(pendingNext.selection ?? null);
      setMasterySelection(pendingNext.selection ?? null);
      setTutorSupport(pendingNext.tutorSupport ?? null);
      setShowWhy(false);
      setShowWorkedExample(false);
      resetPerPartState();
      await fetchAttemptState(pendingNext.attemptId);
      if (canShowDebugUI) {
        void loadMasteryInspect();
      }
      setPendingNext(null);
      return;
    }

    await loadNextQuestion({
      preserveBanner: pendingNext?.type === 'SIMILAR_QUESTION',
    });
    setPendingNext(null);
  }, [
    canShowDebugUI,
    fetchAttemptState,
    loadMasteryInspect,
    loadNextQuestion,
    pendingNext,
    resetPerPartState,
  ]);

  const handleEndSession = async () => {
    if (isEnding) {
      return;
    }
    if (!confirmLeaveWithVolatileWork()) {
      return;
    }

    setIsEnding(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const response = await postEndSession(sessionId);
      if (await isGuardianVerificationBlocked(response)) {
        return;
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to end session.');
        throw new Error(message);
      }

      resetPerPartState();
      navigate(`/sessions/${sessionId}/summary`, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Failed to end session.');
    } finally {
      setIsEnding(false);
    }
  };

  useEffect(() => {
    if (isParent && sessionId) {
      navigate(`/sessions/${sessionId}/summary`, { replace: true });
    }
  }, [isParent, navigate, sessionId]);

  useEffect(() => {
    if (!hasVolatileTutorWork) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasVolatileTutorWork]);

  useEffect(() => {
    if (isParent) {
      return;
    }
    if (restoredDraft) {
      return;
    }
    resumeOpenAttemptOrLoadNext();
  }, [isParent, restoredDraft, resumeOpenAttemptOrLoadNext]);

  useEffect(() => {
    if (!hasMountedFilesPersistenceRef.current) {
      hasMountedFilesPersistenceRef.current = true;
      return undefined;
    }
    const serializationVersion = filesSerializationVersionRef.current + 1;
    filesSerializationVersionRef.current = serializationVersion;
    serializeFilesByPart(filesByPart)
      .then((serialized) => {
        if (filesSerializationVersionRef.current === serializationVersion) {
          if (isSessionDraftMountedRef.current) {
            setSerializedFilesByPart(serialized);
          }
          let draftToPersist = latestSessionDraftRef.current;
          if (!isSessionDraftMountedRef.current) {
            try {
              const storedDraft = JSON.parse(
                sessionStorage.getItem(getTutorSessionDraftKey(sessionId))
              );
              draftToPersist =
                storedDraft?.attemptId === draftToPersist?.attemptId
                  ? storedDraft
                  : null;
            } catch (_error) {
              draftToPersist = null;
            }
          }
          if (draftToPersist) {
            try {
              sessionStorage.setItem(
                getTutorSessionDraftKey(sessionId),
                JSON.stringify({ ...draftToPersist, filesByPart: serialized })
              );
            } catch (_error) {
              // Quota/privacy settings must not make the active lesson unusable.
            }
          }
        }
      })
      .catch(() => {
        // A failed local snapshot must not interrupt answer capture or submission.
      });
  }, [filesByPart, sessionId]);

  latestSessionDraftRef.current =
    sessionId && question && attemptId && !isSessionEnded && !isExhausted
      ? {
          question,
          attemptId,
          filesByPart: serializedFilesByPart,
          inputTypeByPart,
          typedAnswerByPart,
          drawingDraftByPart,
          transcriptionDraftByPart,
          overrideDraftByPart,
          attemptWorkspace,
          tutorSupport,
          selectionMeta,
          masterySelection,
        }
      : null;

  const persistLatestSessionDraft = useCallback(() => {
    const latestDraft = latestSessionDraftRef.current;
    if (!latestDraft) return;
    try {
      sessionStorage.setItem(
        getTutorSessionDraftKey(sessionId),
        JSON.stringify(latestDraft)
      );
    } catch (_error) {
      // Quota/privacy settings must not make the active lesson unusable.
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionDraftWriteTimeoutRef.current) {
      clearTimeout(sessionDraftWriteTimeoutRef.current);
    }
    if (!sessionId || !question || !attemptId || isSessionEnded || isExhausted) {
      return undefined;
    }
    sessionDraftWriteTimeoutRef.current = setTimeout(() => {
      persistLatestSessionDraft();
    }, TUTOR_SESSION_DRAFT_WRITE_DELAY_MS);
    return () => {
      clearTimeout(sessionDraftWriteTimeoutRef.current);
      sessionDraftWriteTimeoutRef.current = null;
    };
  }, [
    attemptId,
    attemptWorkspace,
    inputTypeByPart,
    typedAnswerByPart,
    drawingDraftByPart,
    isExhausted,
    isSessionEnded,
    masterySelection,
    overrideDraftByPart,
    persistLatestSessionDraft,
    question,
    selectionMeta,
    serializedFilesByPart,
    sessionId,
    transcriptionDraftByPart,
    tutorSupport,
  ]);

  useEffect(
    () => () => {
      isSessionDraftMountedRef.current = false;
      persistLatestSessionDraft();
    },
    [persistLatestSessionDraft]
  );

  const clearFeedbackPollTimeout = useCallback(() => {
    if (feedbackPollerRef.current) {
      clearTimeout(feedbackPollerRef.current);
      feedbackPollerRef.current = null;
    }
  }, []);

  const runFeedbackPoll = useCallback(async () => {
    const currentAttemptId = pollMetaRef.current.attemptId;
    if (!currentAttemptId) {
      clearFeedbackPollTimeout();
      return;
    }

    if (document.visibilityState !== 'visible') {
      pollMetaRef.current.delayMs = 5000;
      feedbackPollerRef.current = setTimeout(() => {
        runFeedbackPoll();
      }, 5000);
      return;
    }

    const elapsedMs = Date.now() - (pollMetaRef.current.startedAtMs || Date.now());
    const isStale = elapsedMs >= FEEDBACK_POLL_STALE_AFTER_MS;
    if (isStale) {
      setFeedbackPollStatus((prev) => (
        prev.attemptId === currentAttemptId
          ? { ...prev, stale: true }
          : { attemptId: currentAttemptId, stale: true, pollFailed: false }
      ));
    }

    const result = await fetchAttemptState(currentAttemptId, { fromPoll: true });
    if (!pollMetaRef.current.attemptId || pollMetaRef.current.attemptId !== currentAttemptId) {
      return;
    }

    if (result?.changed) {
      pollMetaRef.current.tries = 0;
      pollMetaRef.current.delayMs = 1000;
      setFeedbackPollStatus({
        attemptId: currentAttemptId,
        stale: false,
        pollFailed: false,
      });
    } else {
      pollMetaRef.current.tries += 1;
      if (pollMetaRef.current.tries >= 10) {
        pollMetaRef.current.delayMs = 4000;
      } else if (pollMetaRef.current.tries >= 5) {
        pollMetaRef.current.delayMs = 2000;
      } else {
        pollMetaRef.current.delayMs = 1000;
      }
      pollMetaRef.current.delayMs = Math.min(
        pollMetaRef.current.delayMs,
        FEEDBACK_POLL_MAX_DELAY_MS
      );
      setFeedbackPollStatus((prev) => (
        prev.attemptId === currentAttemptId
          ? {
              ...prev,
              stale: prev.stale || isStale,
              pollFailed: !!result?.failed,
            }
          : {
              attemptId: currentAttemptId,
              stale: isStale,
              pollFailed: !!result?.failed,
            }
      ));
    }

    feedbackPollerRef.current = setTimeout(() => {
      runFeedbackPoll();
    }, pollMetaRef.current.delayMs);
  }, [clearFeedbackPollTimeout, fetchAttemptState]);

  useEffect(() => {
    const parts = attemptWorkspace?.parts ?? [];
    const hasPendingTutorFeedback = parts.some((part) => {
      const feedback = typeof part?.feedback_markdown === 'string' ? part.feedback_markdown : '';
      const isGenerating = feedback.toLowerCase().includes('generating tutor feedback');
      const isMarked = !!part?.marked_at || part?.status === 'MARKED';
      return isMarked && isGenerating;
    });
    const shouldPoll =
      !!attemptId &&
      attemptWorkspace?.status === 'ACTIVE' &&
      hasPendingTutorFeedback;

    if (!shouldPoll) {
      clearFeedbackPollTimeout();
      pollMetaRef.current = {
        attemptId: null,
        tries: 0,
        delayMs: 1000,
        lastSig: '',
        startedAtMs: 0,
      };
      setFeedbackPollStatus({
        attemptId: '',
        stale: false,
        pollFailed: false,
      });
      return;
    }

    if (pollMetaRef.current.attemptId !== attemptId) {
      pollMetaRef.current = {
        attemptId,
        tries: 0,
        delayMs: 1000,
        lastSig: buildPartsPollSignature(attemptWorkspace),
        startedAtMs: Date.now(),
      };
      setFeedbackPollStatus({
        attemptId,
        stale: false,
        pollFailed: false,
      });
      clearFeedbackPollTimeout();
      feedbackPollerRef.current = setTimeout(() => {
        runFeedbackPoll();
      }, 0);
      return;
    }

    if (!feedbackPollerRef.current) {
      feedbackPollerRef.current = setTimeout(() => {
        runFeedbackPoll();
      }, pollMetaRef.current.delayMs);
    }
  }, [
    attemptId,
    attemptWorkspace,
    buildPartsPollSignature,
    clearFeedbackPollTimeout,
    runFeedbackPoll,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!pollMetaRef.current.attemptId || document.visibilityState !== 'visible') {
        return;
      }
      clearFeedbackPollTimeout();
      feedbackPollerRef.current = setTimeout(() => {
        runFeedbackPoll();
      }, 0);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearFeedbackPollTimeout();
    };
  }, [clearFeedbackPollTimeout, runFeedbackPoll]);

  const finalizeDisabled =
    isFinalizing ||
    isLoading ||
    isExhausted ||
    !attemptId ||
    !question ||
    !allPartsMarked ||
    !!pendingNext;

  const workspacePartByLabel = useMemo(() => {
    if (!question?.parts?.length) {
      return {};
    }
    const parts = attemptWorkspace?.parts ?? [];
    return Object.fromEntries(
      question.parts.map((part) => [
        part.part_label,
        parts.find((entry) => entry.part_id === part.id) ?? null,
      ])
    );
  }, [attemptWorkspace, question]);

  const hasAnyTutorAdjustedPart = useMemo(() => {
    if (!question?.parts?.length) {
      return false;
    }
    return question.parts.some((part) => {
      const workspacePart = workspacePartByLabel[part.part_label];
      return hasTutorAdjustment(workspacePart);
    });
  }, [question, workspacePartByLabel]);

  const attemptTelemetry = useMemo(
    () => getAttemptTelemetry(attemptWorkspace, finalizeTelemetry),
    [attemptWorkspace, finalizeTelemetry]
  );
  const rawPercent = pickFirstDefined(
    attemptTelemetry?.raw_percent,
    attemptTelemetry?.raw_mark_percent
  );
  const adjustedPercent = pickFirstDefined(
    attemptTelemetry?.adjusted_percent,
    attemptTelemetry?.override_adjusted_mark_percent
  );
  const secondsToFinalize = pickFirstDefined(
    attemptTelemetry?.seconds_to_finalize,
    attemptTelemetry?.time_to_finalize_seconds
  );

  useEffect(() => {
    if (!question?.parts?.length) {
      setOverrideDraftByPart({});
      return;
    }

    setOverrideDraftByPart((prev) => {
      const next = {};
      question.parts.forEach((part) => {
        const partLabel = part.part_label;
        const workspacePart = workspacePartByLabel[partLabel];
        const marks = workspacePart?.mark_json || {};
        const prior = prev[partLabel];
        const hasPrior = prior && typeof prior === 'object';
        const tutorNoteFromState =
          typeof workspacePart?.tutor_note === 'string'
            ? workspacePart.tutor_note
            : typeof marks.tutor_note === 'string'
            ? marks.tutor_note
            : '';

        const answerScoreFromState =
          typeof marks.answer_score === 'number' ? String(marks.answer_score) : '';
        const methodScoreFromState =
          typeof marks.method_score === 'number' ? String(marks.method_score) : '';
        const communicationScoreFromState =
          typeof marks.communication_score === 'number'
            ? String(marks.communication_score)
            : '';

        const nextEntry = {
          answer_score: hasPrior ? prior.answer_score ?? answerScoreFromState : answerScoreFromState,
          method_score: hasPrior ? prior.method_score ?? methodScoreFromState : methodScoreFromState,
          communication_score: hasPrior
            ? prior.communication_score ?? communicationScoreFromState
            : communicationScoreFromState,
          tutor_note: hasPrior ? prior.tutor_note ?? tutorNoteFromState : tutorNoteFromState,
        };
        const hasContent =
          nextEntry.answer_score !== '' ||
          nextEntry.method_score !== '' ||
          nextEntry.communication_score !== '' ||
          nextEntry.tutor_note !== '';

        if (hasPrior || hasContent) {
          next[partLabel] = nextEntry;
        }
      });
      return next;
    });
  }, [question, workspacePartByLabel]);

  useEffect(() => {
    if (!canManageOverrides || !attemptId) {
      currentAttemptIdRef.current = '';
      return;
    }
    currentAttemptIdRef.current = String(attemptId);
    if (draftLoadedAttemptRef.current === String(attemptId)) {
      return;
    }

    const loadAttemptDraft = async () => {
      try {
        const response = await getAttemptDraft(attemptId);
        if (!response.ok) {
          draftLoadedAttemptRef.current = String(attemptId);
          return;
        }
        const data = await response.json();
        const drafts = data?.draft_json?.override_drafts_by_part;
        if (drafts && typeof drafts === 'object') {
          isRestoringDraftRef.current = true;
          setOverrideDraftByPart(drafts);
          const draftKey = JSON.stringify(drafts);
          lastSavedDraftKeyRef.current = draftKey;
          currentDraftKeyRef.current = draftKey;
          hasOverrideDraftUserEditRef.current = false;
          setOverrideDraftSaveStatus('saved');
          draftLoadedAttemptRef.current = String(attemptId);
          setTimeout(() => {
            isRestoringDraftRef.current = false;
          }, 0);
        } else {
          draftLoadedAttemptRef.current = String(attemptId);
        }
      } catch (error) {
        draftLoadedAttemptRef.current = String(attemptId);
      }
    };

    loadAttemptDraft();
  }, [attemptId, canManageOverrides]);

  useEffect(() => {
    if (autosaveDraftTimeoutRef.current) {
      clearTimeout(autosaveDraftTimeoutRef.current);
      autosaveDraftTimeoutRef.current = null;
    }
    if (!canManageOverrides || !attemptId) {
      return undefined;
    }
    if (isRestoringDraftRef.current) {
      return undefined;
    }
    if (isOverrideDraftMapEmpty(overrideDraftByPart)) {
      currentDraftKeyRef.current = '';
      setOverrideDraftSaveStatus('idle');
      return undefined;
    }
    const draftKey = JSON.stringify(overrideDraftByPart);
    currentDraftKeyRef.current = draftKey;
    if (draftKey === lastSavedDraftKeyRef.current) {
      setOverrideDraftSaveStatus((current) => (current === 'saving' ? current : 'saved'));
      return undefined;
    }
    if (!hasOverrideDraftUserEditRef.current) {
      setOverrideDraftSaveStatus('idle');
      return undefined;
    }
    setOverrideDraftSaveStatus('dirty');

    autosaveDraftTimeoutRef.current = setTimeout(async () => {
      const saveAttemptId = String(attemptId);
      setOverrideDraftSaveStatus('saving');
      try {
        const response = await putAttemptDraft(attemptId, {
          override_drafts_by_part: overrideDraftByPart,
          updated_at: nowIso(),
        });
        if (response.ok) {
          lastSavedDraftKeyRef.current = draftKey;
          if (
            currentAttemptIdRef.current === saveAttemptId &&
            currentDraftKeyRef.current === draftKey
          ) {
            hasOverrideDraftUserEditRef.current = false;
            setOverrideDraftSaveStatus('saved');
          }
        } else {
          if (
            currentAttemptIdRef.current === saveAttemptId &&
            currentDraftKeyRef.current === draftKey
          ) {
            setOverrideDraftSaveStatus(
              response.status === 403 ? 'permission_denied' : 'failed'
            );
            if (response.status === 403) {
              hasOverrideDraftUserEditRef.current = false;
            }
          }
          debugLog('AttemptDraftSave failed', { attemptId, status: response.status });
        }
      } catch (error) {
        if (
          currentAttemptIdRef.current === saveAttemptId &&
          currentDraftKeyRef.current === draftKey
        ) {
          setOverrideDraftSaveStatus('failed');
        }
        debugLog('AttemptDraftSave error', { attemptId, error: error.message || 'unknown' });
      }
    }, 750);

    return () => {
      if (autosaveDraftTimeoutRef.current) {
        clearTimeout(autosaveDraftTimeoutRef.current);
        autosaveDraftTimeoutRef.current = null;
      }
    };
  }, [attemptId, canManageOverrides, debugLog, isOverrideDraftMapEmpty, overrideDraftByPart]);

  const canRenderDebug = isAdmin && canShowDebugUI;
  const sessionInsightTarget = masteryInspect?.selected_target || masterySelection || selectionMeta || null;
  const workedExampleParts = useMemo(() => buildWorkedExampleParts(tutorSupport), [tutorSupport]);
  const prerequisiteRefreshCard = useMemo(
    () => buildPrerequisiteRefreshCard(tutorSupport),
    [tutorSupport]
  );
  const examStyleSupportCard = useMemo(
    () => buildExamStyleSupportCard(tutorSupport),
    [tutorSupport]
  );
  const canOfferWorkedExample =
    tutorSupport?.support_stage === 'worked_example' && workedExampleParts.length > 0;
  const canRenderPrerequisiteRefreshCard =
    tutorSupport?.selection_intent === 'prerequisite_refresh' && Boolean(prerequisiteRefreshCard);
  const canRenderExamStyleSupportCard =
    !canRenderPrerequisiteRefreshCard && Boolean(examStyleSupportCard);
  const supportStageLabel = formatSupportStageLabel(tutorSupport?.support_stage);
  const selectionIntentLabel = formatSelectionIntentLabel(tutorSupport?.selection_intent);
  const hasSessionInsightSelectionTarget = hasSessionInsightSelection(sessionInsightTarget);
  const sessionInsightReason = hasSessionInsightSelectionTarget
    ? describeSelectionReason(sessionInsightTarget)
    : null;
  const sessionInsightSuccessCriteria = hasSessionInsightSelectionTarget
    ? describeSuccessCriteria(sessionInsightTarget)
    : null;
  const sessionInsightNextStep = hasSessionInsightSelectionTarget
    ? describeNextStepHint(sessionInsightTarget)
    : null;
  const sessionInsightFocusLabel = hasSessionInsightSelectionTarget
    ? getReadableFocusLabel(sessionInsightTarget)
    : null;
  const insightSummary = masteryInspect?.summary || null;
  const secureCount = Number.isFinite(Number(insightSummary?.micro_mastered_count))
    ? Number(insightSummary.micro_mastered_count)
    : null;
  const developingCount = Number.isFinite(Number(insightSummary?.micro_unmastered_count))
    ? Number(insightSummary.micro_unmastered_count)
    : null;
  const studentProgressSummary = secureCount !== null && developingCount !== null
    && secureCount + developingCount > 0
    ? `${secureCount} secure · ${developingCount} still developing`
    : null;
  const devFamily = selectionMeta?.progression_family || null;
  const progressionDebugSelection = masterySelection
    || (pendingNext?.selection ? normalizeMasterySelection(pendingNext.selection) : null);
  const progressionDebugFlags =
    Array.isArray(progressionDebugSelection?.reason_flags) &&
    progressionDebugSelection.reason_flags.length
      ? progressionDebugSelection.reason_flags.join(', ')
      : 'n/a';
  const primaryActionLabel = pendingNext
    ? pendingNext.type === 'SIMILAR_QUESTION'
      ? 'Try Similar Question'
      : 'Continue'
    : 'Finalize';
  const primaryActionHandler = pendingNext ? handleContinueAfterFinalize : handleFinalizeAttempt;
  const primaryActionDisabled = pendingNext ? false : finalizeDisabled;
  const secondaryActionDisabled = pendingNext || isSkipping || isLoading || isExhausted;
  const tertiaryActionDisabled = isEnding;
  const questionPartsCount = Array.isArray(question?.parts) ? question.parts.length : 0;
  const activeSupportHeadline =
    prerequisiteRefreshCard?.prerequisiteMicroSkillName ||
    examStyleSupportCard?.headline ||
    null;
  const supportOverviewDetail = canRenderPrerequisiteRefreshCard
    ? 'The backend has issued an explicit prerequisite detour for this question.'
    : canOfferWorkedExample
    ? 'The backend has issued a worked example stage for the current micro-skill.'
    : canRenderExamStyleSupportCard
    ? examStyleSupportCard?.detail
    : null;
  const inlineSupportContext =
    !canRenderPrerequisiteRefreshCard &&
    !canOfferWorkedExample &&
    (activeSupportHeadline || supportOverviewDetail)
      ? {
          headline: activeSupportHeadline,
          detail: supportOverviewDetail,
        }
      : null;
  const hasTutorGuidance =
    Boolean(sessionInsightReason) ||
    Boolean(sessionInsightSuccessCriteria) ||
    Boolean(sessionInsightNextStep) ||
    Boolean(studentProgressSummary) ||
    Boolean(sessionInsightFocusLabel);
  const learningContextLabels = getLearningContextLabels(question, sessionInsightTarget);
  const sessionFocusTitle = readMeaningfulLabel(runtimeSession?.focus_label);
  const runtimeTitle = sessionFocusTitle || learningContextLabels.title;
  const runtimeSubtitle = sessionFocusTitle
    && sessionFocusTitle !== learningContextLabels.title
    ? learningContextLabels.title || learningContextLabels.subtitle
    : learningContextLabels.subtitle;
  const tutorWhyNow = formatTutorWhyNow({
    selectionIntent: tutorSupport?.selection_intent,
    supportStage: tutorSupport?.support_stage,
    fallback: sessionInsightReason,
  });
  const tutorSuccessFocus = formatTutorSuccessFocus({
    fallback: sessionInsightSuccessCriteria,
  });
  const tutorLikelyNext = formatTutorLikelyNext({
    fallback: sessionInsightNextStep,
  });
  const tutorSupportPath = formatTutorSupportPath({
    supportStageLabel,
    headline: inlineSupportContext?.headline,
    detail: inlineSupportContext?.detail,
  });
  const tutorGuidanceSummary = buildTutorGuidanceSummary({
    whyNow: tutorWhyNow,
    successFocus: tutorSuccessFocus,
  });
  const questionStemText = String(question?.stem_markdown || '').trim();
  const firstPartPrompt = question?.parts?.[0]?.prompt_suffix_markdown || '';
  const topLevelStemVisible = shouldRenderSharedQuestionStem({
    stemMarkdown: question?.stem_markdown,
    firstPartPrompt,
  });
  const bottomBarContext = pendingNext
    ? 'Ready to move on to the next question in this session.'
    : allPartsMarked && question
    ? 'All parts are marked. Finalize when you are ready to continue.'
    : null;

  useEffect(() => {
    const shouldOpenGuidanceByDefault = Boolean(
      question?.is_first_session ||
        question?.show_guidance_by_default
    );
    setShowTutorGuidance(shouldOpenGuidanceByDefault);
  }, [question?.id, question?.is_first_session, question?.show_guidance_by_default]);

  return (
    <div className="page page-shell tutor-page">
      <Card className="session-header-card">
        <div className="session-header-top">
          <div className="stack-xs">
            <h1>{runtimeTitle}</h1>
            {studentDisplayName ? (
              <p className="muted-text session-header-student">Student: {studentDisplayName}</p>
            ) : null}
            {runtimeSubtitle ? <p className="muted-text session-header-subtitle">{runtimeSubtitle}</p> : null}
          </div>
          <div className="tutor-pill-row">
            {banner ? <span className="status-pill status-pill-info">{banner.text}</span> : null}
            {questionPartsCount ? (
              <span className="status-pill status-pill-draft">
                {questionPartsCount} part{questionPartsCount === 1 ? '' : 's'}
              </span>
            ) : null}
            {!isStudent && supportStageLabel ? (
              <span className="status-pill status-pill-info">{supportStageLabel}</span>
            ) : null}
            {!isStudent && selectionIntentLabel ? (
              <span className="status-pill status-pill-draft">{selectionIntentLabel}</span>
            ) : null}
          </div>
        </div>
        {canRenderDebug && TutorSessionDebugHeader ? (
          <Suspense fallback={null}>
            <TutorSessionDebugHeader devFamily={devFamily} selectionMeta={selectionMeta} />
          </Suspense>
        ) : null}
        <div className="session-header-actions">
          {canOfferWorkedExample ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowWorkedExample((prev) => !prev)}
            >
              {showWorkedExample ? 'Hide worked example' : 'Show worked example'}
            </button>
          ) : null}
          <button
            type="button"
            className="session-header-inline-action"
            onClick={() => {
              if (!confirmLeaveWithVolatileWork()) {
                return;
              }
              navigate(`/sessions/${sessionId}/summary`, {
                state: { fromSessionPath: `/sessions/${sessionId}/tutor` },
              });
            }}
          >
            View session summary
          </button>
        </div>
      </Card>

      {isLoading ? <p>Loading question...</p> : null}
      {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
      {infoMessage ? <p className="notice notice-info">{infoMessage}</p> : null}
      {isExhausted ? <p className="notice">No more questions in this session scope.</p> : null}
      {isSessionEnded ? <p className="notice">This tutor session has ended.</p> : null}

      {!isLoading && !errorMessage && question ? (
        <>
          {canRenderDebug && TutorSessionDebugInsight ? (
            <Suspense fallback={null}>
              <TutorSessionDebugInsight
                adjustedPercent={adjustedPercent}
                rawPercent={rawPercent}
                secondsToFinalize={secondsToFinalize}
                selectionMeta={selectionMeta}
                sessionInsightTarget={sessionInsightTarget}
                showWhy={showWhy}
                onToggleShowWhy={() => setShowWhy((prev) => !prev)}
              />
            </Suspense>
          ) : null}

          {hasAnyTutorAdjustedPart ? (
            <p className="notice notice-info">Tutor adjusted marking applied on this question.</p>
          ) : null}

          <Card className="tutor-question-card">
            <div className="tutor-question-layout tutor-question-layout-single">
              {topLevelStemVisible && questionStemText ? (
                <div className="tutor-question-stem-shell" data-testid="shared-question-stem">
                  <div
                    className="markdown-block tutor-question-stem"
                    style={{ maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    <MarkdownRenderer content={question.stem_markdown} />
                  </div>
                </div>
              ) : null}
            </div>
            <div className="part-list workspace-part-list">
              {(question.parts || []).map((part, partIndex) => {
              const partLabel = part.part_label;
              const workspacePart = workspacePartByLabel[partLabel];
              const partStatus = workspacePart?.status ?? 'NOT_STARTED';
              const isSubmitted = partStatus === 'SUBMITTED';
              const { score, max: maxScore, isMarked } = getPartTotals(workspacePart);
              const { answerMax, methodMax, communicationMax } = getPartRubricMaxes(part, workspacePart);
              const rubricMarksAvailable = answerMax + methodMax + communicationMax;
              const marksAvailable =
                typeof part.marks_available === 'number'
                  ? part.marks_available
                  : rubricMarksAvailable;
              const overrideDraft = overrideDraftByPart[partLabel] || {};
              const canShowOverride =
                canManageOverrides &&
                (!!workspacePart?.part_attempt_id ||
                  partStatus === 'SUBMITTED' ||
                  partStatus === 'MARKED');
              const overridingPart = !!isOverridingPart[partLabel];
              const submittingPart = !!isSubmittingPart[partLabel];
              const markingPart = !!isMarkingPart[partLabel];
              const confirmingTranscription = !!isConfirmingTranscription[partLabel];
              const editingTranscription = !!isEditingTranscription[partLabel];
              const skippingPart = !!isSkippingPart[partLabel];
              const overrideDisabled =
                overridingPart ||
                submittingPart ||
                markingPart ||
                skippingPart ||
                isLoading ||
                isExhausted ||
                !attemptId ||
                !workspacePart?.part_attempt_id;
              const tutorNoteFromState =
                typeof workspacePart?.tutor_note === 'string'
                  ? workspacePart.tutor_note
                  : typeof workspacePart?.mark_json?.tutor_note === 'string'
                  ? workspacePart.mark_json.tutor_note
                  : '';
              const isTutorAdjusted = hasTutorAdjustment(workspacePart);
              const confirmationState = getEvidenceConfirmationState(workspacePart);
              const isSkipped = !!workspacePart?.mark_json?.skip;
              const submissionTrust = getSubmissionTrust(workspacePart, canViewSessionInsights);
              const degradedPageState = getDegradedPageStates(workspacePart, submissionTrust);
              const isConfirmingEvidence = !!isConfirmingPart[partLabel];
              const canConfirmEvidence =
                canViewSessionInsights &&
                isMarked &&
                !isSkipped &&
                !confirmationState.isConfirmed &&
                !!workspacePart?.part_attempt_id;
              const shouldEmphasizeConfirmation =
                submissionTrust.lowConfidence ||
                degradedPageState.shouldRender ||
                isTutorAdjusted;
              const feedback = workspacePart?.feedback_markdown ?? '';
              const displayedFeedback = isTutorAdjusted
                ? tutorNoteFromState.trim()
                  ? tutorNoteFromState
                  : feedback
                  ? `Automatic feedback before tutor adjustment:\n\n${normalizeGeneratedFeedbackMarkdown(feedback)}`
                  : ''
                : normalizeGeneratedFeedbackMarkdown(feedback);
              const isFeedbackPending =
                !isTutorAdjusted &&
                typeof displayedFeedback === 'string' &&
                displayedFeedback.toLowerCase().includes('generating tutor feedback');
              const feedbackStatusForAttempt =
                feedbackPollStatus.attemptId === attemptId
                  ? feedbackPollStatus
                  : { stale: false, pollFailed: false };
              const selectedFiles = Array.isArray(filesByPart[partLabel])
                ? filesByPart[partLabel].filter(Boolean)
                : [];
              const hasFile = selectedFiles.length > 0;
              const typedAnswer = typedAnswerByPart[partLabel] || '';
              const hasTypedAnswer = Boolean(typedAnswer.trim());
              // Mirrors the backend gate in `part_attempts.py`: comm_max must
              // be zero too, since a typed answer evidences communication
              // quality no better than it evidences method.
              const allowsTypedAnswer =
                answerMax === 1 && methodMax === 0 && communicationMax === 0;
              // Anything that is not a typed answer went through OCR, so the
              // "check how we read your work" step still applies. Testing for
              // PHOTO/INK positively also hid the panel whenever input_type was
              // absent — which is every pre-existing fixture, and every real
              // submission before the field is populated.
              const isOcrDerivedSubmission = workspacePart?.input_type !== 'TEXT';
              const workspaceTranscriptionLatex =
                typeof workspacePart?.transcription?.latex === 'string'
                  ? workspacePart.transcription.latex
                  : null;
              const hasSubmittedTranscription = Object.prototype.hasOwnProperty.call(
                submittedTranscriptionByPart,
                partLabel
              );
              const transcriptionLatex =
                workspaceTranscriptionLatex ??
                (hasSubmittedTranscription ? submittedTranscriptionByPart[partLabel] : null);
              const hasReadableTranscription = Boolean(transcriptionLatex?.trim());
              const transcriptionPageStates = Array.isArray(workspacePart?.transcription?.confidence?.page_states)
                ? workspacePart.transcription.confidence.page_states
                : [];
              const lowContentPages = transcriptionPageStates.filter(
                (page) => page?.status === 'blank_or_low_content'
              );
              const allPagesLowContent =
                transcriptionPageStates.length > 0 &&
                lowContentPages.length === transcriptionPageStates.length;
              const overrideDraftStatusCopy = getOverrideDraftStatusCopy(overrideDraftSaveStatus);
              const submitDisabled =
                (!hasFile && !hasTypedAnswer) ||
                isMarked ||
                isLoading ||
                isExhausted ||
                submittingPart ||
                markingPart ||
                confirmingTranscription ||
                skippingPart;
              const markDisabled =
                !isSubmitted ||
                isMarked ||
                isFeedbackPending ||
                isLoading ||
                isExhausted ||
                !attemptId ||
                submittingPart ||
                markingPart ||
                skippingPart;
              const skipDisabled =
                isMarked ||
                isFeedbackPending ||
                submittingPart ||
                markingPart ||
                skippingPart ||
                isLoading ||
                isExhausted ||
                !attemptId;

              let statusLabel = 'Draft';
              let statusClassName = 'status-pill-draft';
              if (isMarked && isSkipped) {
                statusLabel = 'Skipped';
                statusClassName = 'status-pill-skipped';
              } else if (isMarked) {
                statusLabel = 'Marked';
                statusClassName = 'status-pill-marked';
              } else if (isSubmitted) {
                statusLabel = 'Submitted';
                statusClassName = 'status-pill-submitted';
              }

              const embeddedPromptMarkdown =
                !topLevelStemVisible && partIndex === 0 && questionStemText
                  ? mergeStemIntoFirstPart(question.stem_markdown, part.prompt_suffix_markdown)
                  : part.prompt_suffix_markdown;

              return (
                <Card key={partLabel} className="part-card part-card-embedded">
                  <div className="part-heading">
                    <div className="stack-xs">
                      <h3>Part {partLabel}</h3>
                      <p className="muted-text">
                        {marksAvailable} mark
                        {marksAvailable === 1 ? '' : 's'} available
                      </p>
                    </div>
                    <div className="tutor-pill-row">
                      <span className={`status-pill ${statusClassName}`}>{statusLabel}</span>
                    </div>
                  </div>
                  {embeddedPromptMarkdown ? (
                    <div
                      className="markdown-block"
                      style={{ maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    >
                      <MarkdownRenderer content={embeddedPromptMarkdown} />
                    </div>
                  ) : null}

                  <div className="part-answer-tools">
                    <p className="muted-text">
                      {allowsTypedAnswer ? 'Type, upload, or draw your answer.' : 'Upload or draw your answer.'}
                    </p>
                    <AnswerCapture
                      idPrefix={`part_${partLabel}`}
                      disabled={
                        isMarked ||
                        submittingPart ||
                        markingPart ||
                        skippingPart ||
                        isFeedbackPending ||
                        isLoading ||
                        isExhausted
                      }
                      selectedFiles={selectedFiles}
                      questionMarkdown={[
                        question.stem_markdown,
                        part.prompt_suffix_markdown,
                      ].filter((value, index, values) =>
                        String(value || '').trim() &&
                        values.findIndex((candidate) =>
                          normalizeComparableText(candidate) === normalizeComparableText(value)
                        ) === index
                      ).join('\n\n')}
                      drawingDraftPages={drawingDraftByPart[partLabel] || null}
                      allowTypedAnswer={allowsTypedAnswer}
                      typedAnswer={typedAnswer}
                      onTypedAnswerChange={(value) => {
                        setTypedAnswerByPart((prev) => ({ ...prev, [partLabel]: value }));
                        if (String(value || '').trim()) {
                          setFilesByPart((prev) => ({ ...prev, [partLabel]: [] }));
                          setInputTypeByPart((prev) => ({ ...prev, [partLabel]: null }));
                          setDrawingDraftByPart((prev) => ({ ...prev, [partLabel]: [] }));
                        }
                        setPartErrors((prev) => ({ ...prev, [partLabel]: '' }));
                      }}
                      onDrawingDraftChange={(pages) => {
                        setDrawingDraftByPart((prev) => ({ ...prev, [partLabel]: pages }));
                      }}
                      onFilesReady={(files, options = {}) => {
                        const incomingFiles = Array.isArray(files) ? files.filter(Boolean) : [];
                        if (incomingFiles.length) {
                          setTypedAnswerByPart((prev) => ({ ...prev, [partLabel]: '' }));
                        }
                        setFilesByPart((prev) => ({
                          ...prev,
                          [partLabel]: (() => {
                            if (options?.inputType !== 'PHOTO') {
                              return incomingFiles;
                            }

                            const currentFiles = Array.isArray(prev[partLabel])
                              ? prev[partLabel].filter(Boolean)
                              : [];
                            if (!incomingFiles.length) {
                              return currentFiles;
                            }

                            const dedupedIncoming = incomingFiles.filter(
                              (nextFile) => !currentFiles.includes(nextFile)
                            );
                            return [...currentFiles, ...dedupedIncoming];
                          })(),
                        }));
                        setInputTypeByPart((prev) => ({
                          ...prev,
                          [partLabel]: options?.inputType,
                        }));
                        setPartErrors((prev) => ({
                          ...prev,
                          [partLabel]: '',
                        }));
                      }}
                      onRemoveFile={(indexToRemove) => {
                        setFilesByPart((prev) => {
                          const current = Array.isArray(prev[partLabel]) ? prev[partLabel] : [];
                          const next = current.filter((_, index) => index !== indexToRemove);
                          return { ...prev, [partLabel]: next };
                        });
                      }}
                      onMoveFile={(indexToMove, direction) => {
                        setFilesByPart((prev) => {
                          const current = Array.isArray(prev[partLabel]) ? prev[partLabel] : [];
                          if (current.length < 2) {
                            return prev;
                          }

                          const delta = direction === 'up' ? -1 : direction === 'down' ? 1 : 0;
                          if (!delta) {
                            return prev;
                          }

                          const nextIndex = indexToMove + delta;
                          if (
                            indexToMove < 0 ||
                            indexToMove >= current.length ||
                            nextIndex < 0 ||
                            nextIndex >= current.length
                          ) {
                            return prev;
                          }

                          const next = [...current];
                          [next[indexToMove], next[nextIndex]] = [next[nextIndex], next[indexToMove]];
                          return { ...prev, [partLabel]: next };
                        });
                      }}
                      onClearFiles={() => {
                        setFilesByPart((prev) => ({ ...prev, [partLabel]: [] }));
                        setInputTypeByPart((prev) => ({ ...prev, [partLabel]: null }));
                      }}
                    />
                  </div>
                  {isSubmitted ? (
                    <p className="notice notice-info">
                      This replaces the submitted answer before marking. It reruns evidence capture; it does not change a marked score.
                    </p>
                  ) : null}
                  {hasFile || hasTypedAnswer ? (
                    <p className="muted-text">
                      {hasTypedAnswer
                        ? 'Your typed answer is ready to submit.'
                        : 'Selected pages are only kept on this page until submitted.'}
                    </p>
                  ) : !isMarked ? (
                    <p id={`submit-part-hint-${partLabel}`} className="muted-text">
                      {allowsTypedAnswer
                        ? 'Type an answer, add a photo, or draw before submitting this part.'
                        : 'Add a photo or drawing before submitting this part.'}
                    </p>
                  ) : null}
                  <div className="part-action-stack" data-testid={`part-actions-${partLabel}`}>
                    {isSubmitted && !isMarked && isOcrDerivedSubmission ? (
                      <section
                        className="transcription-review"
                        aria-labelledby={`transcription-heading-${partLabel}`}
                      >
                        <h4 id={`transcription-heading-${partLabel}`}>Check how we read your work</h4>
                        {lowContentPages.length > 0 ? (
                          <div className="notice notice-warn">
                            {lowContentPages.map((page) => (
                              <p key={page.page_index}>
                                We couldn't read any working on page {page.page_index} — add more detail or replace the page.
                              </p>
                            ))}
                          </div>
                        ) : null}
                        {allPagesLowContent ? null : hasReadableTranscription ? (
                          <>
                            <p className="muted-text">This is what will be marked:</p>
                            {editingTranscription ? (
                              <label className="stack-xs">
                                <span className="field-label">Correct the reading</span>
                                <textarea
                                  aria-label={`Correct transcription for part ${partLabel}`}
                                  rows={Math.max(3, transcriptionLatex.split('\n').length)}
                                  value={transcriptionDraftByPart[partLabel] ?? transcriptionLatex}
                                  onChange={(event) =>
                                    setTranscriptionDraftByPart((prev) => ({
                                      ...prev,
                                      [partLabel]: event.target.value,
                                    }))
                                  }
                                  disabled={confirmingTranscription || markingPart}
                                />
                              </label>
                            ) : (
                              <TranscriptionMathRenderer content={transcriptionLatex} />
                            )}
                          </>
                        ) : (
                          <p className="notice notice-warn">
                            We could not read any writing from this submission. Replace the submission with a clearer photo or drawing. You can still mark it if needed.
                          </p>
                        )}
                        {transcriptionNoticeByPart[partLabel] ? (
                          <p className="notice notice-warn" role="status">
                            {transcriptionNoticeByPart[partLabel]}
                          </p>
                        ) : null}
                        <div className="part-actions transcription-review-actions">
                          {editingTranscription ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                  handleConfirmTranscriptionAndMark(
                                    partLabel,
                                    workspacePart.part_attempt_id
                                  )
                                }
                                disabled={confirmingTranscription || markingPart}
                              >
                                {confirmingTranscription ? 'Saving correction...' : 'Save correction and mark'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-tertiary"
                                onClick={() =>
                                  setIsEditingTranscription((prev) => ({
                                    ...prev,
                                    [partLabel]: false,
                                  }))
                                }
                                disabled={confirmingTranscription || markingPart}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              {!allPagesLowContent ? <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleMarkPart(partLabel)}
                                disabled={markDisabled}
                              >
                                {markingPart ? 'Marking...' : hasReadableTranscription ? 'Looks right — mark it' : 'Mark anyway'}
                              </button> : null}
                              {hasReadableTranscription ? (
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={() => handleFixTranscription(partLabel, transcriptionLatex)}
                                  disabled={markDisabled}
                                >
                                  Fix it
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                        <p className="muted-text">If the image itself is wrong, use Replace submission below.</p>
                      </section>
                    ) : null}
                    <div className="part-actions part-actions-primary">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleSubmitPart(partLabel)}
                        disabled={submitDisabled}
                        aria-describedby={
                          !hasFile && !hasTypedAnswer && !isMarked ? `submit-part-hint-${partLabel}` : undefined
                        }
                      >
                        {submittingPart ? 'Submitting...' : 'Submit part'}
                      </button>
                      {!isSubmitted ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleMarkPart(partLabel)}
                          disabled={markDisabled}
                        >
                          {markingPart ? 'Marking...' : 'Mark part'}
                        </button>
                      ) : null}
                    </div>
                    <div className="part-actions part-actions-secondary">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          const shouldReplace = window.confirm('Replace existing submission with new files?');
                          if (!shouldReplace) {
                            return;
                          }
                          setFilesByPart((prev) => ({ ...prev, [partLabel]: [] }));
                          setInputTypeByPart((prev) => ({ ...prev, [partLabel]: null }));
                          setTypedAnswerByPart((prev) => ({ ...prev, [partLabel]: '' }));
                          setPartErrors((prev) => ({
                            ...prev,
                            [partLabel]: 'Select new images and submit to replace the unmarked submission.',
                          }));
                        }}
                        disabled={!isSubmitted || isMarked || submittingPart || markingPart || isLoading || isExhausted}
                      >
                        {allPagesLowContent ? 'Replace submission / Draw again' : 'Replace submission'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-tertiary"
                        onClick={() => {
                          setFilesByPart((prev) => ({ ...prev, [partLabel]: [] }));
                          setInputTypeByPart((prev) => ({ ...prev, [partLabel]: null }));
                          setTypedAnswerByPart((prev) => ({ ...prev, [partLabel]: '' }));
                        }}
                        disabled={(!hasFile && !hasTypedAnswer) || submittingPart || markingPart || isLoading}
                      >
                        Clear selection
                      </button>
                      <button
                        type="button"
                        className="btn btn-tertiary"
                        onClick={() => handleSkipPart(partLabel)}
                        disabled={skipDisabled}
                      >
                        I don't know
                      </button>
                    </div>
                  </div>

                  <div className="part-meta-summary" data-testid={`part-meta-summary-${partLabel}`}>
                    <span>{isMarked ? formatState('MARKED', 'student') : isSubmitted ? 'Awaiting mark' : hasFile || hasTypedAnswer ? 'Answer ready to submit' : formatState('DRAFT', 'student')}</span>
                  </div>

                  {skippingPart ? <p className="muted-text">Skipping...</p> : null}
                  {isMarked ? <p className="score-line">Score: {score} / {maxScore}</p> : <p className="muted-text">Unmarked</p>}
                  {isMarked ? (
                    <SubmissionTrustCard
                      trust={submissionTrust}
                      pageState={degradedPageState}
                      canViewSessionInsights={canViewSessionInsights}
                      onReviewOverride={
                        canShowOverride && submissionTrust.lowConfidence
                          ? () => handleReviewOverrideOptions(partLabel)
                          : null
                      }
                    />
                  ) : null}
                  {isTutorAdjusted ? <p className="notice notice-info">Tutor adjusted</p> : null}
                  {isMarked ? (
                    <EvidenceConfirmationPanel
                      isConfirmed={confirmationState.isConfirmed}
                      confirmedLowConfidence={confirmationState.confirmedLowConfidence}
                      isTutorAdjusted={isTutorAdjusted}
                      isEmphasized={shouldEmphasizeConfirmation}
                      canConfirm={canConfirmEvidence}
                      isConfirming={isConfirmingEvidence}
                      onConfirm={() => handleConfirmEvidence(partLabel)}
                    />
                  ) : null}

                  {canShowOverride ? (
                    <details
                      className="expando"
                      ref={(node) => {
                        if (node) {
                          overrideSectionRefs.current[partLabel] = node;
                        } else {
                          delete overrideSectionRefs.current[partLabel];
                        }
                      }}
                    >
                      <summary>Override marking</summary>
                      <p className="muted-text">
                        Mark breakdown: answer {answerMax}, method {methodMax}, communication {communicationMax}
                      </p>
                      <div className="override-grid">
                        <div>
                          <label htmlFor={`override_answer_${partLabel}`}>
                            Answer score (0..{answerMax})
                          </label>
                          <input
                            id={`override_answer_${partLabel}`}
                            ref={(node) => {
                              if (node) {
                                overrideFirstInputRefs.current[partLabel] = node;
                              } else {
                                delete overrideFirstInputRefs.current[partLabel];
                              }
                            }}
                            type="number"
                            min={0}
                            max={answerMax}
                            step={1}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={overrideDraft.answer_score ?? ''}
                            onChange={(event) =>
                              handleOverridePartChange(partLabel, 'answer_score', event.target.value)
                            }
                            disabled={overrideDisabled}
                          />
                        </div>
                        {methodMax > 0 ? (
                          <div>
                            <label htmlFor={`override_method_${partLabel}`}>
                              Method score (0..{methodMax})
                            </label>
                          <input
                              id={`override_method_${partLabel}`}
                              type="number"
                              min={0}
                              max={methodMax}
                              step={1}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={overrideDraft.method_score ?? ''}
                              onChange={(event) =>
                                handleOverridePartChange(partLabel, 'method_score', event.target.value)
                              }
                              disabled={overrideDisabled}
                            />
                          </div>
                        ) : null}
                        {communicationMax > 0 ? (
                          <div>
                            <label htmlFor={`override_comm_${partLabel}`}>
                              Communication score (0..{communicationMax})
                            </label>
                          <input
                              id={`override_comm_${partLabel}`}
                              type="number"
                              min={0}
                              max={communicationMax}
                              step={1}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={overrideDraft.communication_score ?? ''}
                              onChange={(event) =>
                                handleOverridePartChange(partLabel, 'communication_score', event.target.value)
                              }
                              disabled={overrideDisabled}
                            />
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <label htmlFor={`override_note_${partLabel}`}>Tutor note</label>
                        <textarea
                          id={`override_note_${partLabel}`}
                          value={overrideDraft.tutor_note ?? ''}
                          onChange={(event) =>
                            handleOverridePartChange(partLabel, 'tutor_note', event.target.value)
                          }
                          disabled={overrideDisabled}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleSaveOverride(partLabel)}
                        disabled={overrideDisabled}
                      >
                        Save override
                      </button>
                      {overrideDraftStatusCopy ? (
                        <p
                          className={`muted-text override-draft-status override-draft-status-${overrideDraftSaveStatus}`}
                          role={overrideDraftSaveStatus === 'failed' ? 'alert' : undefined}
                        >
                          {overrideDraftStatusCopy}
                        </p>
                      ) : null}
                      {overridingPart ? <p className="muted-text">Saving...</p> : null}
                    </details>
                  ) : null}

                  {isMarked && displayedFeedback ? (
                    isFeedbackPending ? (
                      <div className="part-feedback-pending">
                        <p className="muted-text">
                          {feedbackStatusForAttempt.pollFailed
                            ? 'Could not refresh feedback just now. Check the connection; the score shown here is unchanged.'
                            : feedbackStatusForAttempt.stale
                            ? 'Feedback is taking longer than expected. The score is available; you can continue reviewing while feedback finishes.'
                            : 'Tutor feedback is being generated. This can take a moment.'}
                        </p>
                      </div>
                    ) : displayedFeedback.length > 280 ? (
                      <details className="expando part-feedback-block">
                        <summary>View feedback</summary>
                        <div
                          className="markdown-block"
                          style={{ maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          <MarkdownRenderer content={displayedFeedback} />
                        </div>
                      </details>
                    ) : (
                      <div
                        className="markdown-block part-feedback-block"
                        style={{ maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        <MarkdownRenderer content={displayedFeedback} />
                      </div>
                    )
                  ) : null}

                  {partErrors[partLabel] ? (
                    <p role="alert" className="notice notice-error">{partErrors[partLabel]}</p>
                  ) : null}
                </Card>
              );
            })}
            </div>
          </Card>

          {hasTutorGuidance ? (
            <div className="tutor-guidance-panel">
              {tutorGuidanceSummary ? (
                <p className="muted-text tutor-guidance-summary">
                  {tutorGuidanceSummary}{' '}
                  {!showTutorGuidance ? (
                    <button
                      type="button"
                      className="session-header-inline-action"
                      onClick={() => setShowTutorGuidance(true)}
                    >
                      Show more
                    </button>
                  ) : null}
                </p>
              ) : null}
              {showTutorGuidance ? (
                <div className="tutor-guidance-card">
                  <div className="tutor-guidance-list">
                    {tutorWhyNow ? (
                      <p><strong>Why now:</strong> {tutorWhyNow}</p>
                    ) : null}
                    {tutorSuccessFocus ? (
                      <p><strong>Success focus:</strong> {tutorSuccessFocus}</p>
                    ) : null}
                    {tutorLikelyNext ? (
                      <p><strong>Likely next:</strong> {tutorLikelyNext}</p>
                    ) : null}
                    {tutorSupportPath ? (
                      <p><strong>Support path:</strong> {tutorSupportPath}</p>
                    ) : null}
                    {studentProgressSummary ? (
                      <p><strong>Student progress:</strong> {studentProgressSummary}</p>
                    ) : null}
                  </div>
                  {isInspectLoading ? <p className="muted-text">Refreshing tutor guidance...</p> : null}
                  {inspectError && !canRenderDebug ? (
                    <p className="muted-text">Tutor guidance is temporarily limited.</p>
                  ) : null}
                  <button
                    type="button"
                    className="session-header-inline-action tutor-guidance-hide"
                    onClick={() => setShowTutorGuidance(false)}
                  >
                    Hide
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {(canRenderPrerequisiteRefreshCard || (canOfferWorkedExample && showWorkedExample)) ? (
            <div className="tutor-support-stack">
              {canRenderPrerequisiteRefreshCard ? (
                <Card className="tutor-overview-card">
                  <p className="section-title">Prerequisite refresh</p>
                  <p className="muted-text">
                    This stays inside the current tutor flow and only targets the blocking prerequisite skill.
                  </p>
                  <p>
                    <strong>Focus now:</strong> {prerequisiteRefreshCard.prerequisiteMicroSkillName}
                  </p>
                  {prerequisiteRefreshCard.prerequisiteSkillName ? (
                    <p>
                      <strong>Core method:</strong> {prerequisiteRefreshCard.prerequisiteSkillName}
                    </p>
                  ) : null}
                  <p>
                    Secure this prerequisite method so the session can continue once it is safe to move on.
                  </p>
                </Card>
              ) : null}

              {canOfferWorkedExample && showWorkedExample ? (
                <Card className="tutor-overview-card">
                  <p className="section-title">Worked example</p>
                  <p className="muted-text">
                    This support stays inside the current tutor flow and remains tied to the current micro-skill.
                  </p>
                  {workedExampleParts.map((part) => (
                    <div key={part.partLabel} className="stack-sm" style={{ marginTop: 12 }}>
                      <p><strong>Part {part.partLabel}</strong></p>
                      {part.promptSuffixMarkdown ? (
                        <div className="markdown-block">
                          <MarkdownRenderer content={part.promptSuffixMarkdown} />
                        </div>
                      ) : null}
                      <div className="markdown-block">
                        <MarkdownRenderer content={part.methodHint} />
                      </div>
                      <p><strong>Model answer:</strong> <MarkdownRenderer inline content={part.answerMarkdown} /></p>
                    </div>
                  ))}
                </Card>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {canRenderDebug && TutorSessionDebugControls ? (
        <Suspense fallback={null}>
          <TutorSessionDebugControls
            devForceCorrect={devForceCorrect}
            isAdmin={isAdmin}
            isExhausted={isExhausted}
            isLoading={isLoading}
            pendingNext={pendingNext}
            onDevForceCorrectChange={setDevForceCorrect}
            onRequestProgressionBiasedNextQuestion={() =>
              loadNextQuestion({ progressionDebugOverride: true })
            }
          />
        </Suspense>
      ) : null}

      {canRenderDebug && TutorSessionDebugPanelBlock ? (
        <Suspense fallback={null}>
          <TutorSessionDebugPanelBlock
            sessionId={sessionId}
            attemptId={attemptId}
            question={question}
            masterySelection={masterySelection}
            masteryInspect={masteryInspect}
            isInspectLoading={isInspectLoading}
            inspectError={inspectError}
            onRefreshInspect={loadMasteryInspect}
            attemptWorkspace={attemptWorkspace}
            eventLog={eventLog}
          />
        </Suspense>
      ) : null}

      {canRenderDebug && TutorSessionProgressionDebug ? (
        <Suspense fallback={null}>
          <TutorSessionProgressionDebug
            progressionDebugFlags={progressionDebugFlags}
            progressionDebugSelection={progressionDebugSelection}
          />
        </Suspense>
      ) : null}

      {!isSessionEnded ? (
        <BottomBar
          contextText={bottomBarContext}
          primaryLabel={primaryActionLabel}
          onPrimary={primaryActionHandler}
          primaryDisabled={primaryActionDisabled}
          secondaryLabel="Skip question"
          onSecondary={handleSkipQuestion}
          secondaryDisabled={secondaryActionDisabled}
          tertiaryLabel="End session"
          onTertiary={handleEndSession}
          tertiaryDisabled={tertiaryActionDisabled}
        />
      ) : null}
    </div>
  );
}

export default TutorSessionPage;
